import { recordingAudioCopy } from "./recordingAudioCopy";
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const SUPPORTED_LANGUAGES = ['en', 'de', 'fr'];
export const DEFAULT_LANGUAGE = 'en';

export const LANGUAGE_LABELS = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Francais',
};

const STORAGE_KEY = 'it_locale';

const translations = {
  en: {
    'upload.connectionRetrying': 'Connection interrupted. Retrying…',
    'upload.connectionOffline': 'Offline. The upload will resume when you reconnect.',
    'upload.alreadyActive': 'This file is already uploading. Return to the active upload, or select it again after that upload stops.',
    'upload.connectionReconnecting': 'Reconnecting…',
    'upload.connectionResumeHint': 'Your progress is saved. The upload will continue from the last received block.',
    'upload.selectSameToResume': 'Upload paused. Select the same file to continue from your saved progress.',
    'upload.authUnavailable': 'Your sign-in could not be checked right now. Please try again shortly.',
    'import.captions_lookup': 'Checking available YouTube captions…',
    'import.captions_fetch': 'Downloading YouTube captions…',
    'import.captions_save': 'Saving your YouTube transcript…',
    'import.youtubeWaitingHint': 'We’re checking YouTube captions. You can leave this page and find the result in your workspace.',
    'common.language': 'Language',
    'common.back': 'Back',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.copy': 'Copy',
    'common.delete': 'Delete',
    'common.download': 'Download',
    'common.email': 'Email',
    'common.password': 'Password',
    'common.loading': 'Loading...',
    'common.sending': 'Sending...',
    'common.updating': 'Updating...',
    'common.save': 'Save',
    'common.upgrade': 'Upgrade',
    'common.error': 'Error',
    'common.signIn': 'Sign in',
    'common.signOut': 'Sign Out',
    'common.signUp': 'Sign Up',
    'common.profile': 'Profile',
    'common.admin': 'Admin',
    'common.brandTagline': 'Audio & Video -> Text',
    'common.done': 'Done',
    'common.processing': 'Processing...',
    'common.revoke': 'Revoke',
    'billing.intervalLabel': 'Billing interval',
    'billing.monthly': 'Monthly',
    'billing.annual': 'Annual',
    'billing.savePercent': 'Save {{percent}}%',
    'billing.perMonth': '/month',
    'billing.billedMonthly': 'Billed monthly. Cancel anytime.',
    'billing.billedAnnual': 'Billed {{annualTotal}}/year. Cancel anytime.',
    'billing.upgradeAnnualDiscount': 'Annual - save {{percent}}%',
    'billing.upgradeMonthly': 'Upgrade monthly',
    'billing.startMonthly': 'Start monthly for {{price}}',
    'billing.continueMonthly': 'Continue transcribing for {{price}}/month',
    'billing.saveWithAnnual': 'Save {{percent}}% with annual',
    'billing.annualTeaser': 'From {{price}}/month when billed annually.',
    'billing.cancelAnytime': 'Cancel anytime.',
    'billing.transcribeFileNow': 'Transcribe this file now',

    'auth.unexpected': 'An unexpected error occurred. Please try again.',
    'auth.invalidCredentials': 'Incorrect email or password. Please try again.',
    'auth.emailNotConfirmed': 'Please confirm your email address before logging in.',
    'auth.loginTitle': 'Sign in to InstantTranscriber',
    'auth.loginSubtitle': 'Sign in to continue to {{project}}',
    'auth.resetTitle': 'Reset Password',
    'auth.resetSubtitle': "Enter your email and we'll send you a reset link.",
    'auth.resetSent': 'Reset link sent! Please check your email to continue.',
    'auth.resetFailed': 'Failed to send reset email. Please try again.',
    'auth.sendReset': 'Send reset link',
    'auth.signInApple': 'Sign in with Apple',
    'auth.signInGoogle': 'Sign in with Google',
    'auth.signInEmail': 'Sign in with Email',
    'auth.forgotPassword': 'Forgot password?',
    'auth.noAccount': "Don't have an account?",
    'auth.newToProject': 'New to {{project}}?',
    'auth.createOne': 'Create one',
    'auth.signUpNow': 'Sign up now',
    'auth.createAccount': 'Create Account',
    'auth.signupSubtitle': 'Get started with {{project}}',
    'auth.userExists': 'This email is already in use. Please use a different email.',
    'auth.weakPassword': 'The password is too weak. Please use a stronger password (at least 6 characters).',
    'auth.signupSuccess': 'Account created successfully! Please check your email to confirm your account before signing in.',
    'auth.skip': 'Skip for now',
    'auth.signUpGoogle': 'Sign up with Google',
    'auth.signUpEmail': 'Sign up with Email',
    'auth.passwordMin': 'Password (min. 8 characters)',
    'auth.alreadyAccount': 'Already have an account?',
    'auth.legal': 'By continuing, you acknowledge that you accept {{project}}',
    'auth.terms': 'Terms of Use',
    'auth.privacy': 'Privacy Policy',
    'auth.or': 'or',
    'auth.and': 'and',
    'auth.backToSignIn': 'Back to sign in',
    'auth.newPassword': 'New Password',
    'auth.confirmNewPassword': 'Confirm New Password',
    'auth.passwordLength': 'Password must be at least 8 characters long.',
    'auth.passwordMismatch': 'Passwords do not match.',
    'auth.passwordUpdated': 'Password updated successfully!',
    'auth.passwordUpdatedRedirect': 'Password updated successfully! Redirecting to your dashboard...',
    'auth.passwordUpdateFailed': 'Failed to update password: {{message}}',
    'auth.chooseNewPassword': 'Choose a new password for your account',
    'auth.checkingResetLink': 'Checking your reset link...',
    'auth.invalidResetLink': 'This reset link is invalid or has expired. Please request a new one.',
    'auth.updatePassword': 'Update Password',
    'auth.changePassword': 'Change Password',
    'auth.createSecurePassword': 'Create a new secure password',
    'auth.backToProfile': 'Back to Profile',
    'auth.changeEmail': 'Change Email',
    'auth.updateEmailAddress': 'Update your email address',
    'auth.emailRequired': 'Please enter an email address.',
    'auth.emailInvalid': 'Please enter a valid email address.',
    'auth.emailSame': 'New email must be different from your current email.',
    'auth.emailChangeSent': 'A verification email has been sent to your new email address. Please verify it to complete the update.',
    'auth.emailChangeFailed': 'Failed to initiate email update: {{message}}',
    'auth.emailChangeWarning': "After changing your email, you'll need to verify your new email address and sign in again.",
    'auth.newEmail': 'New Email Address',
    'auth.updateEmail': 'Update Email',
    'auth.verifyTitle': 'Verify Your Identity',
    'auth.verifySubtitle': 'For security reasons, please confirm your password',
    'auth.verifyWarning': 'This action requires recent authentication. Please verify your identity to continue.',
    'auth.currentPassword': 'Your Current Password',
    'auth.verifyGoogle': 'Verify with Google',
    'auth.verifyPassword': 'Verify Password',
    'auth.verifying': 'Verifying...',
    'auth.authFailed': 'Authentication failed: {{message}}',
    'auth.googleFailed': 'Google sign-in failed',
    'auth.deleteTitle': 'Delete Account',
    'auth.deleteSubtitle': 'This action cannot be undone',
    'auth.deleteWarningTitle': 'Warning: Permanent Account Deletion',
    'auth.deleteWarningBody': 'Deleting your account will permanently remove all your data, including:',
    'auth.deleteHistory': 'All transcription history',
    'auth.deleteSettings': 'Account settings and preferences',
    'auth.deletePremium': 'Premium subscription status (if applicable)',
    'auth.deleting': 'Deleting Account...',
    'auth.confirmDelete': 'Confirm Delete',
    'auth.cancelDelete': 'Cancel and Return to Profile',
    'auth.accountDeleted': 'Your account has been deleted.',
    'auth.deleteFailed': 'Account deletion did not complete. Please try again.',

    'overview.loginTitle': 'Please log in to access your dashboard',
    'workspace.title': 'Your transcription workspace',
    'workspace.firstSubtitle': 'Start with a file, a recording, or a public link.',
    'workspace.returningSubtitle': 'Start something new or pick up where you left off.',
    'workspace.premiumActive': 'Premium active',
    'workspace.newTranscription': 'New transcription',
    'workspace.formats': 'MP3, MP4, M4A, WAV, and other audio or video files',
    'workspace.freeLimits': 'Up to 35 minutes / 50 MB per file',
    'workspace.premiumLimits': 'Up to 10 hours / 3 GB per file',
    'workspace.recordAudio': 'Microphone',
    'workspace.recordTabMic': 'Tab + mic',
    'workspace.recordTab': 'Browser tab',
    'workspace.urlTitle': 'YouTube or media link',
    'workspace.urlPlaceholder': 'Paste a YouTube or public media URL',
    'workspace.urlHint': 'Public media files, Drive, Dropbox or SoundCloud. YouTube imports available captions.',
    'workspace.library': 'Your transcripts',
    'workspace.exportAll': 'Export all',
    'workspace.emptyTitle': 'Your first transcript starts here',
    'workspace.emptyBody': 'Choose a file or paste a link above. Your transcripts will appear here, ready to read and export.',
    'workspace.loadingHistory': 'Loading your transcripts…',
  'workspace.searchingHistory': 'Loading matching transcripts…',
  'workspace.loadMore': 'Load older transcripts',
  'workspace.loadingMore': 'Loading more…',
  'workspace.historyPageHint': 'Search covers your complete archive.',
  'upload.preparationBackgroundHint': 'Your file is saved on the server. Preparation continues if you close this page. Return to your workspace to start it.',
  'import.downloadProgressKnown': 'Downloaded {{received}} of {{total}} MB',
  'import.downloadProgress': 'Downloaded {{received}} MB',
  'import.longSourceHint': 'This recording is longer than the free 35-minute limit. Once it is ready, you can choose an available preview or upgrade.',

    'workspace.historyUnavailable': 'History is temporarily unavailable',
    'workspace.historyError': 'Could not load your transcripts. Please try again.',
    'workspace.retry': 'Retry',
    'workspace.search': 'Search transcripts',
    'workspace.filter': 'Filter by status',
    'workspace.allFiles': 'All transcripts',
    'workspace.readyFiles': 'Ready to read',
    'workspace.activeFiles': 'In progress',
    'workspace.noResults': 'No matching transcripts',
    'workspace.noResultsBody': 'Try another name or change the status filter.',
    'workspace.clearFilters': 'Clear filters',
    'workspace.openTranscript': 'Open transcript',
    'workspace.openPreview': 'Open preview',
    'workspace.openFile': 'Open transcript: {{name}}',
    'workspace.deleteFile': 'Delete recording: {{name}}',
    'workspace.yourPlan': 'Your plan',
    'workspace.dailyAllowance': 'Daily allowance',
    'workspace.freeRemaining': '{{remaining}} of {{limit}} free transcripts left today',
    'workspace.premiumIntro': 'For longer recordings and more frequent transcription.',
    'workspace.premiumAfterValue': 'Keep going with longer files and unlimited transcription.',
    'workspace.premiumAtLimit': 'Continue today with unlimited transcription.',
    'workspace.openingCheckout': 'Opening secure checkout…',
    'workspace.limitReached': 'Your free transcripts are used up today. Choose Premium to continue, or return after the daily reset.',
    'workspace.nearLimit': 'One free transcript left today. Need more?',
    'workspace.seePremium': 'See Premium',
    'workspace.accountMenu': 'Account menu',
    'overview.loginBody': 'You need to be logged in to view and manage your transcriptions.',
    'overview.heroTitle': 'Start a transcript in 30 seconds.',
    'overview.heroTitlePremium': 'Start a transcript in 30 seconds.',
    'overview.subtitleFree': '3 free transcriptions today \u00b7 No credit card needed.',
    'overview.subtitlePremium': 'Premium active \u00b7 Unlimited transcriptions \u00b7 Up to 10h / 3GB per file',
    'overview.heroHint': 'Upload a file, record live audio, or paste a public link.',
    'overview.limitReached': 'Daily free limit reached.',
    'overview.nearLimit': 'Almost at your daily free limit.',
    'overview.usedAllFree': "You've used all free transcriptions for today.",
    'overview.almostOutFree': "You're almost out of free transcriptions today.",
    'overview.unlock': 'Unlock unlimited, up to 10h / file and 3 GB / file.',
    'overview.goUnlimited': 'Unlock unlimited transcription',
    'overview.freePlan': 'Free Plan',
    'overview.freeUsage': 'You have used {{used}} of {{limit}} daily transcriptions',
    'overview.freeLeftFull': 'You have {{count}} free {{unit}} left today.',
    'overview.freeLeftRemaining': '{{count}} left today - keep going.',
    'overview.transcriptionSingular': 'transcription',
    'overview.transcriptionPlural': 'transcriptions',
    'overview.premium': 'Premium',
    'overview.mostPopular': 'Most popular',
    'overview.pricePeriod': '/ month',
    'overview.monthlyPrice': '$9.99/month',
    'overview.billedMonthly': 'Cancel anytime',
    'overview.moneyBack': '30-day money-back guarantee \u00b7 Secured by Stripe',
    'overview.shortBenefits': 'Unlimited transcriptions \u00b7 summaries \u00b7 More accurate speaker labels',
    'overview.upgradeToPremium': 'Upgrade to Premium',
    'overview.upgradeMonthly': 'Upgrade to monthly',
    'overview.upgradeAnnual': 'Upgrade annually',
    'overview.premiumPlan': 'Premium Plan',
    'overview.premiumEnjoy': 'Enjoy unlimited transcriptions with premium features',
    'overview.freeFeature1': '3 transcriptions per day',
    'overview.freeFeature2': 'Max 35 minutes per file',
    'overview.freeFeature3': 'Max 50 MB per file',
    'overview.freeFeature4': 'First transcript summary included',
    'overview.proFeature1': 'Unlimited transcriptions',
    'overview.proFeature2': 'Max 10 hours per file',
    'overview.proFeature3': 'Max 3 GB per file',
    'overview.proFeature4': 'Transcript summaries',
    'overview.proFeature5': 'More accurate speaker labels',
    'overview.proFeature6': 'Priority support',
    'overview.uploadTitle': 'Upload or record audio',
    'overview.dragDrop': 'Drag & drop files here',
    'overview.selectComputer': '...or select from your computer',
    'overview.chooseFiles': 'Choose files',
    'overview.sample': 'Try a sample',
    'overview.supports': 'Supports .mp3, .m4a, .wav, .flac, .ogg, .aiff, .mov, .mp4, YouTube captions, Google Drive, and SoundCloud',
    'overview.or': 'OR',
    'overview.recordMic': 'Click to record audio directly',
    'overview.recordMicInfo': 'Record live audio using your microphone',
    'overview.recordTabMic': 'Record tab + microphone',
    'overview.recordTabMicTitle': 'Record another tab and microphone (Chrome/Edge)',
    'overview.recordTabMicInfo': 'Capture a tab and your microphone in one audio file',
    'overview.recordTab': 'Record another tab',
    'overview.recordTabTitle': 'Record another tab (Chrome/Edge)',
    'overview.recordTabInfo': 'Capture audio from a browser tab (Chromium only)',
    'overview.pasteLink': 'Paste a link',
    'overview.urlPlaceholder': 'YouTube, Google Drive, SoundCloud, podcast, or direct media link',
    'overview.urlLabel': 'Audio or video URL',
    'overview.importLink': 'Import link',
    'overview.import': 'Import',
    'overview.importing': 'Importing...',
    'overview.publicLinksOnly': 'Public links only. Supports YouTube videos with captions, direct media files, Google Drive, SoundCloud, and podcasts.',
    'overview.freeReassure': '3 free transcriptions/day - No credit card needed',
    'overview.freeLeftUpgrade': 'You have {{count}} free {{unit}} left today. Upgrade anytime for unlimited use.',
    'overview.recent': 'Recent Transcriptions',
    'overview.noFiles': "You haven't uploaded any files yet. Upload your first audio file to get started!",
    'overview.viewTranscription': 'View Transcription',
    'overview.deleteFile': 'Delete File',
    'overview.copy': 'Copy',
    'overview.downloadTxt': 'Download TXT',
    'overview.downloadSrt': 'Download SRT',
    'overview.downloadVtt': 'Download VTT',
    'overview.downloadDocx': 'Download DOCX',
    'overview.downloadPdf': 'Download PDF',
    'overview.dropFiles': 'Drop your audio files to upload',
    'overview.dropSub': "We'll start the upload right away.",
    'overview.uploaded': 'Uploaded:',
    'overview.duration': 'Duration:',
    'overview.shortSummary': 'Short summary',
    'overview.eta': 'Remaining:',
    'overview.etaEstimating': 'Estimating…',
    'overview.etaAfterStart': 'Time estimate available after transcription starts.',
    'overview.phase.queued': 'Waiting for transcription to start…',
    'overview.phase.preparing': 'Preparing audio…',
    'overview.phase.transcribing': 'Transcribing…',
    'overview.phase.diarizing': 'Identifying speakers…',
    'overview.phase.saving': 'Saving transcript…',
    'overview.phase.completed': 'Transcript ready',
    'overview.lang': 'Lang:',
    'overview.statusQueuedWarming': 'queued / warming up',
    'overview.statusStarting': 'starting transcription...',
    'overview.statusReady': 'ready to start',
    'overview.statusRequiresUpgrade': 'upgrade required',
    'overview.previewReady': "3-minute preview ready",
    'overview.savedRequiresPremium': "Your recording is saved. The free plan supports files up to 35 minutes. Choose Premium to transcribe the complete recording. We will try to start it automatically once Premium is active; you can also start it from your saved recordings.",
    'billing.signupPremium': "Sign in & choose Premium",
    'billing.signupPremiumHint': "Sign in or create an account first, then continue to Premium checkout.",
    'overview.previewNoticePremium': "This transcript contains the first 3 minutes. Start a full transcription to include the complete recording.",
    'overview.previewNotice': "This free preview covers the first 3 minutes. Upgrade to Premium for the complete transcript.",
    'overview.previewStarted': "Your recording is saved. We are automatically transcribing the first 3 minutes for free. This counts as one of your 3 daily transcriptions. Upgrade to Premium for the complete recording.",
    'overview.startFull': "Transcribe full recording",

    'overview.upgradeToStart': 'Upgrade to start',
    'overview.startingTranscription': 'Starting...',
    'overview.showSpeakers': 'Show speakers',
    'overview.showTimestamps': 'Show timestamps',
    'overview.transcriptViews': 'Transcript views',
    'overview.transcriptTab': 'Transcript',
    'overview.summaryTab': 'Summary',
    'overview.summaryPending': 'Summary is still being prepared.',
    'overview.noSummary': 'No summary available for this transcript.',
    'overview.speakerReviewProcessing': 'Improving speaker labels...',
    'overview.speakerReviewProcessingDetail': 'Your transcript is ready. We are still improving speaker labels and will update this view automatically.',
    'overview.useEnhancedSpeakers': 'Enhanced speaker labels',
    "overview.speakerNames": "Speaker names",
    "overview.renameSpeakers": "Rename speakers",
    "overview.speakerNamesHint": "Names update every speaker label and export. Leave a name empty to reset it.",
    "overview.speakerNamesSaved": "Speaker names saved.",
    "overview.speakerNamesUncertain": "Some name spellings are uncertain. You can correct them.",
    "overview.speakerNameVariants": "Spellings in the transcript: {{names}}",
    "overview.speakerNameInvalid": "Use a name of 1–60 characters without line breaks.",
    "overview.speakerNamesChanged": "Speaker labels changed. Please reopen the transcript and try again.",
    "overview.speakerNamesSaveFailed": "Could not save speaker names. Your edits are still here; please try again.",
    "overview.speakerNamesSaving": "Saving…",
    "overview.saveSpeakerNames": "Save names",
    'overview.copyText': 'Copy Text',
    'overview.downloadCompact': 'Download',
    'overview.speakersCompact': 'Speakers',
    'overview.timestampsCompact': 'Timestamps',
    'overview.downloadAs': 'Download as',
    'overview.bulkDownload': 'Bulk download',
    'overview.bulkCompletedCount': '{{count}} completed transcripts',
    'overview.bulkFormat': 'Format',
    'overview.bulkIncludeSummaries': 'Include summaries when available',
    'overview.bulkDownloadZip': 'Download ZIP',
    'overview.bulkPreparingShort': 'Preparing...',
    'overview.bulkPreparing': 'Preparing {{current}}/{{total}}',
    'overview.bulkZipping': 'Creating ZIP...',
    'overview.bulkDownloadReady': 'ZIP download started.',
    'overview.bulkDownloadPartial': 'ZIP download started with {{count}} export errors.',
    'overview.bulkNoFiles': 'No transcript files could be exported.',
    'overview.txtPlain': 'TXT (plain)',
    'overview.txtSummary': 'TXT (summary)',
    'overview.summaryTxtOnly': 'Summary can only be downloaded as TXT.',
    'overview.withSpeakers': '(with speakers)',
    'overview.noSpeakers': '(no speakers)',
    'overview.withTimecodes': '(with timecodes)',
    'overview.copied': 'Copied to clipboard!',
    'overview.copyErrorTitle': 'Copy Error',
    'overview.copyErrorBody': 'Could not copy to clipboard. Please select the text and copy manually.',
    'overview.exportError': 'Export Error',
    'overview.exportFailed': 'Export failed',
    'overview.downloadingTxt': 'Downloading TXT...',
    'overview.downloadingFormat': 'Downloading {{format}}...',
    'overview.uploadError': 'Upload Error',
    'overview.upgradeRequired': 'Upgrade required',
    'overview.startError': 'Transcription Start Error',
    'overview.uploadComplete': 'Upload complete - ready to start.',
    'overview.fileUploaded': 'File uploaded! Transcription in progress...',
    'overview.recordingUploaded': 'Recording uploaded! Transcription in progress...',
    'overview.uploadCanceled': 'Upload canceled.',
    'overview.uploadsCanceled': 'Uploads canceled.',
    'overview.recordingUploadError': 'Recording Upload Error',
    'overview.deletionError': 'Deletion Error',
    'overview.deletionFailed': 'Deletion failed: {{message}}',
    'overview.sampleError': 'Sample Error',
    'overview.sampleFailed': 'Could not load the sample file.',
    'overview.invalidUrl': 'Please paste a valid http(s) URL.',
    'overview.spotifyTitle': 'Spotify Episodes Not Supported',
    'overview.resolvingPodcast': 'Resolving podcast...',
    'overview.podcastResolveError': 'Podcast Resolution Error',
    'overview.podcastResolveFailed': 'Could not resolve the podcast link.',
    'overview.linkStarted': 'Link imported and transcription started!',
    'overview.linkImported': 'Link imported - ready to start.',
    'overview.importError': 'Import Error',
    'overview.importFailed': 'Could not import the URL.',
    'overview.tabUnavailable': 'Tab Capture Not Available',
    'overview.tabUnavailableBody': "Your browser does not allow capturing another tab's audio. Please use Chrome/Edge or record with a microphone.",
    'overview.micRequired': 'Microphone Access Required',
    'overview.micRequiredBody': 'Please allow microphone access to use the recording feature. Check your browser permissions and try again.',
    'overview.loadingTranscription': 'Loading transcription...',
    'overview.loadingRetry': 'Loading transcription... (retry {{attempt}}/{{max}})',
    'overview.loadingDetail': 'Loading detailed view...',
    'overview.fetchError': 'Error fetching file details: {{message}}',
    'overview.gotItStart': "Got it - I'll start as soon as the upload finishes.",
    'overview.finalizingUpload': "Your upload is complete. Transcription will start once the audio is ready.",
    'overview.noTranscription': 'No transcription available',
    'overview.detectedLanguage': 'Detected Language',

    'status.error': 'error',
    'status.completed': 'completed',
    'status.processing': 'processing',
    'status.queued': 'queued',
    'status.uploaded': 'uploaded',
    'status.unknown': 'unknown',

    'upload.addAudio': 'Add audio',
    'upload.recordTabMicAudio': 'Record Tab + Microphone',
    'upload.recordTabAudio': 'Record Tab Audio',
    'upload.recordTranscribe': 'Record & Transcribe',
    'upload.selectPodcast': 'Select Podcast Episode',
    'upload.close': 'Close',
    'upload.optionsAll': 'Options below apply to all files.',
    'upload.waitingServer': 'Waiting for server...',
    'upload.uploadingPct': 'Uploading... {{percent}}%',
    'upload.filesSelected': '{{count}} files selected',
    'upload.untitled': 'Untitled',
    'upload.file': 'File:',
    'upload.size': 'Size:',
    'upload.unknown': 'Unknown',
    'upload.queued': 'Queued',
    'upload.uploading': 'Uploading',
    'upload.uploaded': 'Uploaded',
    'upload.startFailed': 'Start failed',
    'upload.canceled': 'Canceled',
    'upload.error': 'Error',
    'upload.freeSizeExceeded': 'This file exceeds the Free plan limit (50 MB). Upgrade to upload up to 3 GB and 10 h per file.',
    'upload.premiumSizeExceeded': 'This file exceeds the Premium plan limit (3 GB). Choose a smaller file or split it before uploading.',
    'upload.finalizingUpload': 'Preparing audio…',
    'upload.transferComplete': 'Upload complete',
    'upload.preparation.preparing': 'Preparing audio…',
    'upload.preparation.waiting': 'Waiting to prepare your audio…',
    'upload.preparation.checking': 'Checking your audio…',
    'upload.preparation.extracting': 'Extracting audio from video…',
    'upload.preparation.converting': 'Converting your audio…',
    'upload.preparation.saving': 'Saving your file…',
    'upload.preparationElapsed': '{{seconds}} seconds elapsed',
    'upload.preparationHint': 'Long recordings can take a little longer to prepare. You don’t need to upload again.',
    'upload.podcastLink': 'Podcast link',
    'upload.podcastPlaceholder': 'Paste Apple Podcasts / show page / RSS link',
    'upload.resolve': 'Resolve',
    'upload.resolving': 'Resolving...',
    'upload.feed': 'Feed',
    'upload.directEpisode': 'Direct episode link detected',
    'upload.change': 'Change URL',
    'upload.episodes': 'Episodes',
    'upload.filterEpisodes': 'Filter episodes...',
    'upload.title': 'Title',
    'upload.date': 'Date',
    'upload.transcript': 'Transcript',
    'upload.hasTranscript': 'has transcript',
    'upload.selected': 'Selected',
    'upload.select': 'Select',
    'upload.none': 'none',
    'upload.pickEpisode': 'Pick an episode first',
    'upload.resolveFailed': 'Failed to resolve link',
    'upload.importFailed': 'Failed to import/transcribe',
    'upload.language': 'Language:',
    'upload.searchLanguages': 'Search languages...',
    'upload.advanced': 'Advanced Options',
    'upload.preferPublisher': 'Prefer publisher transcript (if available)',
    'upload.publisherTranscriptInfo': 'If a publisher transcript is available and enabled, it will import instantly.',
    'upload.normalTranscriptionInfo': "Otherwise we'll fetch the audio enclosure and start a normal transcription job.",
    'upload.speakerIdentification': 'Speaker identification',
    'upload.numberSpeakers': 'Number of speakers:',
    'upload.autoDetect': 'Auto-detect',
    'upload.oneSpeaker': '1 speaker',
    'upload.nSpeakers': '{{count}} speakers',
    'upload.importTranscribe': 'Import & Transcribe',
    'upload.working': 'Working...',
    'upload.startRecording': 'Start Recording',
    'upload.startingRecording': 'Starting recording…',
    'upload.startTabMicCapture': 'Start Tab + Mic Capture',
    'upload.startTabCapture': 'Start Tab Capture',
    'upload.recording': 'Recording',
    'upload.recordingTabMic': 'Recording (Tab + Mic)',
    'upload.recordingTab': 'Recording (Tab)',
    'upload.recordingComplete': 'Recording Complete',
    'upload.duration': 'Duration',
    'upload.resume': 'Resume',
    'upload.pause': 'Pause',
    'upload.transcribe': 'Transcribe',
    'upload.captureAnotherTabMic': 'Capture Another Tab + Mic',
    'upload.captureAnotherTab': 'Capture Another Tab',
    'upload.recordAgain': 'Record Again',
    'upload.uploadingRecording': 'Uploading recording... {{percent}}%',
    'upload.generateTimestamps': 'Generate timestamps',
    'upload.premiumPitch': 'Go Premium: unlimited transcriptions, up to 10h/file, 3 GB/file, and priority support.',
    'upload.startTranscription': 'Start Transcription',
    'upload.startAll': 'Start Transcription (All)',
    'upload.startAllCountdown': 'Start All ({{count}}s)',
    'upload.startOneCountdown': 'Start Transcription ({{count}}s)',
    'upload.autoStartAllCountdown': 'Start transcription for all files in {{count}}s',
    'upload.autoStartOneCountdown': 'Start transcription in {{count}}s',
    'upload.autoStartAll': 'Automatically start transcription for all files 10 seconds after uploading/importing',
    'upload.autoStartOne': 'Automatically start transcription 10 seconds after uploading/importing',
    'upload.waitingUpload': 'Waiting for upload...',
    'upload.noTabAudio': "No tab/system audio was shared. Please select a tab and enable 'Share tab audio' in the picker.",
    'upload.tabMicCaptureFailed': 'Could not start tab + microphone capture. Please allow microphone access, choose a tab, and enable shared tab audio.',
    'upload.tabCaptureFailed': 'Could not start tab capture. Please ensure your browser supports it and try again (Chrome/Edge).',
    'upload.micFailed': 'Could not access microphone. Please check browser permissions.',
    'upload.recordingProblemTitle': 'Recording could not start',
    'upload.recording.micDenied': 'Microphone access was cancelled or blocked. Allow microphone access in your browser’s site settings, then try again.',
    'upload.recording.tabDenied': 'Tab sharing was cancelled or blocked. Try again and choose a browser tab with “Share tab audio” enabled.',
    'upload.recording.noMic': 'No microphone was found. Connect one and check your device’s input settings.',
    'upload.recording.deviceBusy': 'The audio device could not be opened. Check whether another application is using it, then try again.',
    'upload.recording.missingAudio': 'No tab audio was shared. Select a browser tab and enable “Share tab audio”.',
    'upload.recording.browser': 'Recording is unavailable in this browser. Try another browser or upload an audio file.',
    'upload.recording.constraints': 'This audio device cannot use the selected recording settings. Try another microphone or upload a file.',
    'upload.recording.startFailed': 'Recording could not start. Try again or upload an audio file.',

    "profile.backToWorkspace": "Back to transcriptions",
    "profile.accountSecurity": "Account & security",
    "profile.planBilling": "Plan & billing",
    "profile.emailVerified": "Email verified",
    "profile.emailPending": "Email not verified yet",
    "profile.securityHint": "We’ll ask you to sign in again before changing your email or password.",
    "profile.providerManaged": "You sign in with {{provider}}. Manage your sign-in details with {{provider}}.",
    "profile.active": "Active",
    "profile.premiumWebAccess": "Your Premium features are ready to use in the web app.",
    "profile.upgradeTitle": "More room with Premium",
    "profile.openingCheckout": "Opening checkout…",
    "profile.openingBilling": "Opening billing…",
    "profile.billingHint": "Update payment details, change your plan or cancel your subscription.",
    "profile.planLoadFailed": "We couldn’t load your plan details. Please try again.",
    "profile.developerTools": "Developer tools & API",
    "profile.developerHint": "API keys, usage and integrations",
    "profile.dataControlsHint": "Reset your transcript history or delete your account",
    "profile.apiPlan": "API Plan",
    "profile.apiPlanAccess": "Includes 100 API audio hours per billing month. Web transcriptions don’t count against API usage.",
    "profile.apiPlanHint": "Need more API capacity? Explore the API Plan with 100 included audio hours per month.",
    "profile.exploreApiPlan": "Explore API Plan",
    "profile.checkingApiPlan": "Checking API Plan…",
    "profile.apiMonthlyQuota": "Monthly API usage",
    "profile.plan": "Plan",
    "profile.included": "Included",
    "profile.used": "Used",
    "profile.remaining": "Remaining",
    "profile.resets": "Resets",
    "profile.refreshing": "Refreshing…",
    "profile.refresh": "Refresh",
    "profile.apiQuotaUnavailable": "API usage unavailable. Try refreshing.",
    "profile.apiKeyLabel": "API key label (optional)",
    "profile.creatingApiKey": "Creating key…",
    "profile.loadingApiLogs": "Loading API activity…",
    "profile.viewApiLogs": "View API activity",
    "profile.recentApiJobs": "Recent API transcriptions",
    "profile.hide": "Hide",
    "profile.noApiJobs": "No API transcriptions yet.",
    "profile.apiKeysLoadFailed": "We couldn’t load your API keys. Please try again.",
    "profile.monthlyCap": "Monthly API overage cap (USD)",
    'profile.startPremium': 'Get Premium',
    'profile.title': 'Account settings',
    'profile.subtitle': 'Manage your account settings and subscription',
    'profile.userInfo': 'User Information',
    'profile.email': 'Email:',
    'profile.premiumSubscription': 'Premium Subscription (Unlimited Transcriptions)',
    'profile.mcpDescription': 'Connect an AI assistant to transcribe audio and retrieve YouTube captions. MCP shares your API allowance.',
    'profile.mcpConnections': 'Manage connected assistants',
    'profile.mcpSetup': 'Setup guide',
    'profile.premiumAccess': 'You have access to all premium features, including longer transcriptions, priority support, and 15 API/MCP hours per Stripe billing month.',
    'profile.appleManagedShort': 'This subscription is managed by Apple. Use your iPhone/iPad Settings -> Apple ID -> Subscriptions to update or cancel.',
    'profile.freePlan': 'Free Plan',
    'profile.dailyUsage': 'Daily usage: {{used}} of {{limit}} transcriptions ({{remaining}} remaining today)',
    'profile.securityEmail': 'Security & Email',
    'profile.emailUnverified': 'Your email is not verified. Please verify your email for account security.',
    'profile.resendVerification': 'Resend Verification Email',
    'profile.apiKeys': 'API Keys',
    'profile.apiDescription': 'API keys let you call the transcription API directly. Keep them secret. You can revoke a key anytime.',
    'profile.apiPlaceholder': 'Optional label (e.g., CLI, Zapier)',
    'profile.createApiKey': 'Create API key',
    'profile.loadingKeys': 'Loading keys...',
    'profile.noKeys': 'No API keys yet.',
    'profile.created': 'Created:',
    'profile.lastUsed': 'Last used:',
    'profile.newApiKey': 'New API key (shown only once!)',
    'profile.accountData': 'Account Data',
    'profile.accountWarning': 'The following actions will affect your stored data and cannot be undone.',
    'profile.noUser': 'No user is logged in. Please log in to view your profile.',
    'profile.resetAccount': 'Reset Account Data',
    'profile.deleteAccount': 'Delete Account',
    'profile.changePassword': 'Change Password',
    'profile.changeEmail': 'Change Email',
    'profile.manageSubscription': 'Manage Subscription',
    'profile.viewInvoices': 'View invoices',
    'profile.hideInvoices': 'Hide invoices',
    'profile.invoiceHistory': 'Invoice history',
    'profile.invoice': 'Invoice',
    'profile.openInvoice': 'View invoice',
    'profile.downloadInvoice': 'Download PDF',
    'profile.openInvoiceLabel': 'View invoice {{number}}',
    'profile.downloadInvoiceLabel': 'Download invoice {{number}} as PDF',
    'profile.loadingInvoices': 'Loading invoices…',
    'profile.noInvoices': 'No invoices yet.',
    'profile.invoicesFailed': 'Could not load invoices. Please try again.',
    'profile.retryInvoices': 'Try again',
    'profile.olderInvoices': 'Load older invoices',
    'profile.invoiceStatus.paid': 'Paid',
    'profile.invoiceStatus.open': 'Unpaid',
    'profile.invoiceStatus.draft': 'Draft',
    'profile.invoiceStatus.void': 'Voided',
    'profile.invoiceStatus.uncollectible': 'Uncollectible',
    'profile.cancellationScheduled': 'Your subscription is set to cancel. Premium access ends on {{date}}. Manage your subscription to review or change this.',
    'profile.billingNeedsAttention': 'Your subscription payment needs attention. Open billing to review your payment details.',
    'profile.usePremium': 'Use Premium: transcribe a file or link',
    'overview.soundCloudSingleTrack': 'For SoundCloud, paste one public track link (soundcloud.com/artist/track), not a profile or playlist.',
  "upload.limitTitle": "Upload needs your attention",
  "upload.dailyLimit": "You have used today’s 3 guest transcriptions. Sign in to continue with your account, or come back after the daily reset.",
  "upload.accountDailyLimit": "Your free daily allowance is used up. Choose Premium to continue today, or come back after the daily reset.",
  "upload.resetAt": "Free transcriptions are available again on {{time}}",
  "upload.savedAfterLimit": "Your recording is saved. Start it from your history after the reset or after upgrading; you do not need to upload it again.",
  "upload.savedFiles": "View saved recording",
  "upload.continueToday": "Continue today with Premium",
  "upload.pendingLimit": "Three guest uploads are waiting. Start or delete one in your transcription history before adding another file.",
  "upload.pendingSessions": "Five unfinished uploads are saved. Select one of those files again to resume it, or cancel an active upload before adding another.",
  "upload.selectAgain": "This saved upload has expired or ended. Select the file again to start a new upload.",
  "upload.sessionExpired": "This session has ended. Sign in to continue, or create a free account if you do not have one yet.",
  "upload.rateLimited": "Too many requests right now. Please wait before trying again.",
  "upload.createAccount": "Create free account",
  "upload.signIn": "Sign in",
  "upload.reviewPending": "Review waiting files",
  "overview.noSpeechTitle": "No speech was recognized",
  "overview.noSpeechHint": "The recording was processed, but no spoken words were recognized. Check that the audio is audible and the microphone recorded the intended speaker, then upload another recording.",
  "overview.silentAudioHint": "The audio is silent or extremely quiet. Check your microphone and recording volume, then upload a new recording.",
  "overview.speechWithoutTextHint": "Speech was detected, but no words were recognized. Check the audio and select its original language when starting another transcription.",
  "overview.audioReadHint": "The audio could not be decoded for diagnosis. Try playing the original file and uploading it again in MP3, WAV or M4A format.",
  "overview.resultReadHint": "The transcript files could not be retrieved. Your recording may contain speech; this result does not establish that it was silent.",
  "overview.uploadAnotherRecording": "Upload another recording",
  "overview.speakerReviewRetrying": "Your transcript is ready. The AI review is temporarily delayed; we are trying again automatically.",
  "overview.speakerReviewFailedTitle": "AI review unavailable",
  "overview.speakerReviewFailed": "Your transcript is ready, but the additional AI review could not finish. You can read and export the transcript and correct speaker names yourself.",
    "import.backgroundWaiting": "Your link import is queued",
    "import.backgroundDownloading": "Downloading the linked audio",
    "import.backgroundPreparing": "Preparing the imported audio",
    "import.backgroundReconnecting": "Reconnecting to your import…",
    "import.backgroundHint": "The import runs in the background. You can leave this page and return to your workspace.",
    "import.backgroundReady": "Your link import is ready",
    "import.alreadyRunning": "This recording is already being transcribed. You can follow its progress below.",
    "import.backgroundContinue": "Continue with this import",
    "import.importedFile": "Imported audio",
   "import.captionsMissing": "This video has no available captions. Try another video or upload the original audio file.",
    "import.originalCaptionsMissing": "Captions are not available in the original language. Upload the original audio to transcribe it.",
    "import.videoUnavailable": "This video is private, unavailable or restricted. Try a public video or upload an audio file.",
    "import.invalidVideo": "Paste a link to one YouTube video, rather than a channel or playlist.",
    "import.connectionFailed": "The source could not be reached. Try again or upload the original audio file.",
    "import.dailyLimit": "You have reached today’s free limit. Come back tomorrow or choose Premium to continue.",
    "import.planLimit": "This import exceeds your plan’s limits. Choose a smaller file or upgrade to continue.",
    "import.fileTooLarge": "This file is too large. Upload a smaller file or split the audio.",
    "import.freeFileTooLarge": "This file exceeds the Free plan’s 50 MB limit. Premium supports up to 3 GB per file.",
    "import.premiumFileTooLarge": "This file exceeds Premium’s 3 GB limit. Upload a smaller file or split the audio.",
    "import.abovePremiumLimit": "This file exceeds the 3 GB maximum, including Premium. Upload a smaller file or split the audio.",
    "import.accessDenied": "This source cannot be accessed. Check that the link is public or upload the original audio file.",
    "import.sourceFailed": "This link could not be imported. Check that it plays without signing in or upload the original audio file.",
    "import.invalidSource": "Paste a public HTTP or HTTPS link to one audio or video file.",
    "import.sourceNotPublic": "This file is not public. Enable sharing with anyone who has the link, or upload the original file.",
    "import.sourceAccessDenied": "The source refused access to our server. The link may need permission, may have expired, or may block downloads.",
    "import.sourceAccessHelp": "If you have the original audio or video file, upload it directly. To import by link, check its sharing permissions or use a fresh download link before trying again.",
    "import.sourceNotFound": "This file is no longer available. Check the link or upload the original file.",
    "import.unsupportedMedia": "This link did not return an audio or video file. Use a direct media link or upload the original file.",
    "import.webpageMedia": "This link opens a webpage. Copy the download link that opens the audio or video itself, or upload the original file.",
    "import.driveMedia": "Use a shared audio or video file in Google Drive. Google Docs and folder links cannot be transcribed; upload the original media instead.",
    "import.driveAccess": "Google Drive did not allow this file to be downloaded. Check sharing and copy the complete file link again, then retry.",
    "import.driveAccessHelp": "In Drive, open Share → General access → Anyone with the link → Viewer. Allow viewers to download the file and copy the full link using Copy link. If the file must stay private, download it yourself and upload the original here.",
    "import.upgradeContinue": "See Premium and continue import",
    "import.upgradeResumeHint": "This tab keeps your link. After upgrading, the import continues once automatically. Files over 3 GB still need to be reduced first.",
    "import.platformMedia": "This platform page did not provide downloadable media. Download your audio or video and upload the file. YouTube links use available captions.",
    "import.singleFile": "Paste a link to one file or track, rather than a folder or playlist.",
    "import.redirectFailed": "This link redirects too many times. Use the final public media link or upload the original file.",
    "import.sourceTimeout": "The source took too long to respond. Try again or upload the original file.",
    "import.sourceRateLimited": "The source is temporarily limiting downloads. Try again later or upload the original file.",
    "import.sourceUnavailable": "Import from this source is temporarily unavailable. Upload the original file.",
    "import.soundCloudNotPublic": "This SoundCloud track is not accessible to our server. Use a public track that plays without signing in, or upload the original audio.",
    "import.soundCloudNotFound": "This SoundCloud track is no longer available. Check the track link or upload the original audio.",
    "import.soundCloudNoAudio": "SoundCloud did not provide playable audio for this track. Upload the original audio instead.",
    "import.soundCloudRegion": "This SoundCloud track is unavailable in our server’s region. Upload audio you can access instead.",
    "import.linkRestored": "Your link has been kept. Select Import when you are ready to continue.",
    "import.captionsImported": "YouTube captions imported. Your transcript is ready.",
    "import.loadingCaptions": "Getting your YouTube transcript…",
    "import.loadingLink": "Importing your link…",
    "import.loadingDetail": "Keep this window open. Your result will appear here.",
    "import.failedTitle": "This link could not be imported",
    "workspace.progressUnavailable": "Progress could not be refreshed. We are trying to reconnect.",
    "workspace.quotaUnavailable": "Your transcription allowance could not be loaded. Try again to see your current allowance.",
    "recovery.title": "We could not display this page",
    "recovery.body": "Your saved transcripts are still available. Try opening this view again, or reload the page if the problem persists.",
    "recovery.retry": "Try again",
    "recovery.reload": "Reload page",
    'overview.importRecovery': 'Check that this is one public audio or video item and that it plays without signing in. If the source blocks import, upload the original file you have permission to use.',
    'overview.uploadOriginal': 'Upload the original file instead',
    'profile.subscribe': 'Subscribe to Premium',
    'profile.signOut': 'Sign Out',
    'profile.verificationSent': 'Verification email sent.',
    'profile.resetConfirmTitle': 'Reset Account Data',
    'profile.resetConfirmMessage': 'Are you sure you want to reset your account data? All your transcription history will be removed. This action cannot be undone.',
    'profile.resetConfirm': 'Yes, Reset Data',
    'profile.resetSuccess': 'Your account data has been reset successfully.',
    'profile.resetFailed': 'Failed to reset account data. Please try again.',
    'profile.revokeConfirm': 'Revoke this API key? This cannot be undone.',
    'profile.apiKeyError': 'API Key Error',
    'profile.apiCreateFailed': 'Failed to create API key.',
    'profile.apiRevokeFailed': 'Failed to revoke API key.',
    'profile.subscriptionError': 'Premium Subscription Error',
    'profile.subscriptionFailed': 'Subscription failed: {{message}}',
    'profile.manageTitle': 'Manage Subscription',
    'profile.appleManaged': 'Your subscription is managed by Apple. To update or cancel, open Settings on your iPhone/iPad -> Apple ID -> Subscriptions.',
    'profile.billingError': 'Subscription Management Error',
    'profile.billingFailed': 'Failed to access billing portal: {{message}}',
    'profile.copiedTitle': 'Copied',
    'profile.copiedBody': 'API key copied to clipboard.',
    'profile.copyFailedTitle': 'Copy failed',
    'profile.copyFailedBody': 'Could not copy to clipboard.',
  },
};

const de = {
    'upload.connectionRetrying': 'Verbindung unterbrochen. Wird erneut versucht…',
    'upload.connectionOffline': 'Offline. Der Upload wird fortgesetzt, sobald du wieder verbunden bist.',
    'upload.alreadyActive': 'Diese Datei wird bereits hochgeladen. Kehre zum laufenden Upload zurück oder wähle sie erneut aus, sobald dieser beendet ist.',
    'upload.connectionReconnecting': 'Verbindung wird wiederhergestellt…',
    'upload.connectionResumeHint': 'Dein Fortschritt ist gespeichert. Der Upload wird ab dem zuletzt empfangenen Block fortgesetzt.',
    'upload.selectSameToResume': 'Upload pausiert. Wähle dieselbe Datei aus, um ab dem gespeicherten Fortschritt fortzufahren.',
    'upload.authUnavailable': 'Deine Anmeldung konnte gerade nicht geprüft werden. Bitte versuche es gleich noch einmal.',
    'import.captions_lookup': 'Verfügbare YouTube-Untertitel werden geprüft…',
    'import.captions_fetch': 'YouTube-Untertitel werden geladen…',
    'import.captions_save': 'Dein YouTube-Transkript wird gespeichert…',
    'import.youtubeWaitingHint': 'Wir prüfen die YouTube-Untertitel. Du kannst die Seite verlassen und das Ergebnis später im Arbeitsbereich öffnen.',
  'workspace.title': 'Dein Transkriptionsbereich',
  'workspace.firstSubtitle': 'Starte mit einer Datei, einer Aufnahme oder einem öffentlichen Link.',
  'workspace.returningSubtitle': 'Starte etwas Neues oder mache mit deinen Transkripten weiter.',
  'workspace.premiumActive': 'Premium aktiv',
  'workspace.newTranscription': 'Neues Transkript',
  'workspace.formats': 'MP3, MP4, M4A, WAV und weitere Audio- oder Videodateien',
  'workspace.freeLimits': 'Bis zu 35 Minuten / 50 MB pro Datei',
  'workspace.premiumLimits': 'Bis zu 10 Stunden / 3 GB pro Datei',
  'workspace.recordAudio': 'Mikrofon',
  'workspace.recordTabMic': 'Tab + Mikro',
  'workspace.recordTab': 'Browser-Tab',
  'workspace.urlTitle': 'YouTube- oder Medienlink',
  'workspace.urlPlaceholder': 'YouTube- oder öffentlichen Medienlink einfügen',
  'workspace.urlHint': 'Öffentliche Mediendateien, Drive, Dropbox oder SoundCloud. YouTube importiert verfügbare Untertitel.',
  'workspace.library': 'Deine Transkripte',
  'workspace.exportAll': 'Alle exportieren',
  'workspace.emptyTitle': 'Hier beginnt dein erstes Transkript',
  'workspace.emptyBody': 'Wähle oben eine Datei oder füge einen Link ein. Deine Transkripte erscheinen hier zum Lesen und Exportieren.',
  'workspace.loadingHistory': 'Deine Transkripte werden geladen…',
  'workspace.searchingHistory': 'Passende Transkripte werden geladen…',
  'workspace.loadMore': 'Ältere Transkripte laden',
  'workspace.loadingMore': 'Weitere werden geladen…',
  'workspace.historyPageHint': 'Die Suche berücksichtigt dein gesamtes Archiv.',
  'upload.preparationBackgroundHint': 'Deine Datei liegt auf dem Server. Die Vorbereitung läuft weiter, wenn du diese Seite schließt. Kehre zum Arbeitsbereich zurück, um sie zu starten.',
  'import.downloadProgressKnown': '{{received}} von {{total}} MB heruntergeladen',
  'import.downloadProgress': '{{received}} MB heruntergeladen',
  'import.longSourceHint': 'Diese Aufnahme überschreitet das kostenlose Limit von 35 Minuten. Sobald sie bereit ist, kannst du eine verfügbare Vorschau wählen oder upgraden.',

  'workspace.historyUnavailable': 'Der Verlauf ist vorübergehend nicht verfügbar',
  'workspace.historyError': 'Deine Transkripte konnten nicht geladen werden. Bitte versuche es erneut.',
  'workspace.retry': 'Erneut versuchen',
  'workspace.search': 'Transkripte durchsuchen',
  'workspace.filter': 'Nach Status filtern',
  'workspace.allFiles': 'Alle Transkripte',
  'workspace.readyFiles': 'Fertig zum Lesen',
  'workspace.activeFiles': 'In Bearbeitung',
  'workspace.noResults': 'Keine passenden Transkripte',
  'workspace.noResultsBody': 'Versuche einen anderen Namen oder ändere den Statusfilter.',
  'workspace.clearFilters': 'Filter zurücksetzen',
  'workspace.openTranscript': 'Transkript öffnen',
  'workspace.openPreview': 'Vorschau öffnen',
  'workspace.openFile': 'Transkript öffnen: {{name}}',
  'workspace.deleteFile': 'Aufnahme löschen: {{name}}',
  'workspace.yourPlan': 'Dein Tarif',
  'workspace.dailyAllowance': 'Tageskontingent',
  'workspace.freeRemaining': 'Heute noch {{remaining}} von {{limit}} kostenlosen Transkripten',
  'workspace.premiumIntro': 'Für längere Aufnahmen und häufigere Transkriptionen.',
  'workspace.premiumAfterValue': 'Weiterarbeiten mit längeren Dateien und unbegrenzter Transkription.',
  'workspace.premiumAtLimit': 'Heute unbegrenzt weiter transkribieren.',
  'workspace.openingCheckout': 'Sicherer Checkout wird geöffnet…',
  'workspace.limitReached': 'Deine kostenlosen Transkripte für heute sind aufgebraucht. Mit Premium weiterarbeiten oder nach dem täglichen Zurücksetzen wiederkommen.',
  'workspace.nearLimit': 'Heute ist noch ein kostenloses Transkript übrig. Brauchst du mehr?',
  'workspace.seePremium': 'Premium ansehen',
  'workspace.accountMenu': 'Kontomenü',
  'common.language': 'Sprache',
  'common.back': 'Zuruck',
  'common.cancel': 'Abbrechen',
  'common.close': 'Schliessen',
  'common.confirm': 'Bestaetigen',
  'common.copy': 'Kopieren',
  'common.delete': 'Loschen',
  'common.download': 'Herunterladen',
  'common.email': 'E-Mail',
  'common.password': 'Passwort',
  'common.loading': 'Laden...',
  'common.sending': 'Senden...',
  'common.updating': 'Aktualisieren...',
  'common.save': 'Speichern',
  'common.upgrade': 'Upgrade',
  'common.error': 'Fehler',
  'common.signIn': 'Einloggen',
  'common.signOut': 'Ausloggen',
  'common.signUp': 'Registrieren',
  'common.profile': 'Profil',
  'common.admin': 'Admin',
  'common.brandTagline': 'Audio & Video -> Text',
  'common.done': 'Fertig',
  'common.processing': 'Wird verarbeitet...',
  'common.revoke': 'Widerrufen',
  'billing.intervalLabel': 'Abrechnungsintervall',
  'billing.monthly': 'Monatlich',
  'billing.annual': 'Jaehrlich',
  'billing.savePercent': '{{percent}}% sparen',
  'billing.perMonth': '/Monat',
  'billing.billedMonthly': 'Monatlich abgerechnet. Jederzeit kuendbar.',
  'billing.billedAnnual': '{{annualTotal}}/Jahr abgerechnet. Jederzeit kuendbar.',
  'billing.upgradeAnnualDiscount': 'Jaehrlich - {{percent}}% sparen',
  'billing.upgradeMonthly': 'Monatlich upgraden',
  'billing.startMonthly': 'Monatlich fuer {{price}} starten',
  'billing.continueMonthly': 'Weiter transkribieren fuer {{price}}/Monat',
  'billing.saveWithAnnual': '{{percent}}% sparen mit jaehrlich',
  'billing.annualTeaser': 'Ab {{price}}/Monat bei jaehrlicher Abrechnung.',
  'billing.cancelAnytime': 'Jederzeit kuendbar.',
  'billing.transcribeFileNow': 'Diese Datei jetzt transkribieren',
  'auth.loginTitle': 'Bei InstantTranscriber anmelden',
  'auth.loginSubtitle': 'Melde dich an, um mit {{project}} fortzufahren',
  'auth.resetTitle': 'Passwort zurucksetzen',
  'auth.resetSubtitle': 'Gib deine E-Mail ein und wir senden dir einen Reset-Link.',
  'auth.resetSent': 'Reset-Link gesendet. Bitte prufe deine E-Mails.',
  'auth.resetFailed': 'Reset-E-Mail konnte nicht gesendet werden. Bitte versuche es erneut.',
  'auth.sendReset': 'Reset-Link senden',
  'auth.signInApple': 'Mit Apple einloggen',
  'auth.signInGoogle': 'Mit Google einloggen',
  'auth.signInEmail': 'Mit E-Mail einloggen',
  'auth.forgotPassword': 'Passwort vergessen?',
  'auth.noAccount': 'Noch kein Konto?',
  'auth.newToProject': 'Neu bei {{project}}?',
  'auth.createOne': 'Konto erstellen',
  'auth.signUpNow': 'Jetzt registrieren',
  'auth.createAccount': 'Konto erstellen',
  'auth.signupSubtitle': 'Starte mit {{project}}',
  'auth.userExists': 'Diese E-Mail wird bereits verwendet. Bitte nutze eine andere E-Mail.',
  'auth.weakPassword': 'Das Passwort ist zu schwach. Bitte nutze mindestens 6 Zeichen.',
  'auth.signupSuccess': 'Konto erfolgreich erstellt. Bitte bestatige deine E-Mail, bevor du dich einloggst.',
  'auth.skip': 'Vorerst uberspringen',
  'auth.signUpGoogle': 'Mit Google registrieren',
  'auth.signUpEmail': 'Mit E-Mail registrieren',
  'auth.passwordMin': 'Passwort (mind. 8 Zeichen)',
  'auth.alreadyAccount': 'Du hast schon ein Konto?',
  'auth.legal': 'Mit dem Fortfahren akzeptierst du die',
  'auth.terms': 'Nutzungsbedingungen',
  'auth.privacy': 'Datenschutzrichtlinie',
  'auth.or': 'oder',
  'auth.and': 'und',
  'auth.backToSignIn': 'Zuruck zum Login',
  'auth.newPassword': 'Neues Passwort',
  'auth.confirmNewPassword': 'Neues Passwort bestatigen',
  'auth.passwordLength': 'Das Passwort muss mindestens 8 Zeichen lang sein.',
  'auth.passwordMismatch': 'Die Passwoerter stimmen nicht uberein.',
  'auth.passwordUpdated': 'Passwort erfolgreich aktualisiert.',
  'auth.passwordUpdatedRedirect': 'Passwort erfolgreich aktualisiert. Weiterleitung zum Dashboard...',
  'auth.passwordUpdateFailed': 'Passwort konnte nicht aktualisiert werden: {{message}}',
  'auth.chooseNewPassword': 'Wahle ein neues Passwort fur dein Konto',
  'auth.checkingResetLink': 'Reset-Link wird gepruft...',
  'auth.invalidResetLink': 'Dieser Reset-Link ist ungultig oder abgelaufen. Bitte fordere einen neuen an.',
  'auth.updatePassword': 'Passwort aktualisieren',
  'auth.changePassword': 'Passwort andern',
  'auth.createSecurePassword': 'Erstelle ein neues sicheres Passwort',
  'auth.backToProfile': 'Zuruck zum Profil',
  'auth.changeEmail': 'E-Mail andern',
  'auth.updateEmailAddress': 'Aktualisiere deine E-Mail-Adresse',
  'auth.emailRequired': 'Bitte gib eine E-Mail-Adresse ein.',
  'auth.emailInvalid': 'Bitte gib eine gultige E-Mail-Adresse ein.',
  'auth.emailSame': 'Die neue E-Mail muss sich von der aktuellen unterscheiden.',
  'auth.emailChangeSent': 'Eine Bestatigungs-E-Mail wurde an deine neue Adresse gesendet.',
  'auth.emailChangeFailed': 'E-Mail-Update konnte nicht gestartet werden: {{message}}',
  'auth.emailChangeWarning': 'Nach der Anderung musst du deine neue E-Mail bestatigen und dich erneut anmelden.',
  'auth.newEmail': 'Neue E-Mail-Adresse',
  'auth.updateEmail': 'E-Mail aktualisieren',
  'auth.verifyTitle': 'Identitat bestatigen',
  'auth.verifySubtitle': 'Bitte bestatige aus Sicherheitsgrunden dein Passwort',
  'auth.verifyWarning': 'Diese Aktion erfordert eine aktuelle Authentifizierung.',
  'auth.currentPassword': 'Dein aktuelles Passwort',
  'auth.verifyGoogle': 'Mit Google verifizieren',
  'auth.verifyPassword': 'Passwort verifizieren',
  'auth.verifying': 'Wird verifiziert...',
  'auth.authFailed': 'Authentifizierung fehlgeschlagen: {{message}}',
  'auth.googleFailed': 'Google-Login fehlgeschlagen',
  'auth.deleteTitle': 'Konto loschen',
  'auth.deleteSubtitle': 'Diese Aktion kann nicht ruckgangig gemacht werden',
  'auth.deleteWarningTitle': 'Warnung: dauerhaftes Loschen des Kontos',
  'auth.deleteWarningBody': 'Wenn du dein Konto loschst, werden dauerhaft entfernt:',
  'auth.deleteHistory': 'Alle Transkriptionsverlaufe',
  'auth.deleteSettings': 'Kontoeinstellungen und Praferenzen',
  'auth.deletePremium': 'Premium-Abostatus (falls vorhanden)',
  'auth.deleting': 'Konto wird geloscht...',
  'auth.confirmDelete': 'Loschen bestatigen',
  'auth.cancelDelete': 'Abbrechen und zuruck zum Profil',
  'auth.accountDeleted': 'Dein Konto wurde geloscht.',
  'auth.deleteFailed': 'Das Konto konnte nicht vollstandig geloscht werden. Bitte versuche es erneut.',
  'auth.unexpected': 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.',
  'auth.invalidCredentials': 'Falsche E-Mail oder falsches Passwort. Bitte versuche es erneut.',
  'auth.emailNotConfirmed': 'Bitte bestatige deine E-Mail-Adresse, bevor du dich einloggst.',
  'overview.loginTitle': 'Bitte logge dich ein, um dein Dashboard zu nutzen',
  'overview.loginBody': 'Du musst eingeloggt sein, um deine Transkriptionen zu sehen und zu verwalten.',
  'overview.heroTitle': 'Starte ein Transkript in 30 Sekunden.',
  'overview.heroTitlePremium': 'Starte ein Transkript in 30 Sekunden.',
  'overview.subtitleFree': '3 kostenlose Transkriptionen heute \u00b7 keine Kreditkarte erforderlich.',
  'overview.subtitlePremium': 'Premium aktiv \u00b7 unbegrenzte Transkriptionen \u00b7 bis zu 10 h / 3 GB pro Datei',
  'overview.heroHint': 'Lade eine Datei hoch, nimm Audio live auf oder fuege einen oeffentlichen Link ein.',
  'overview.usedAllFree': 'Du hast alle kostenlosen Transkriptionen fuer heute genutzt.',
  'overview.almostOutFree': 'Du hast fast alle kostenlosen Transkriptionen fuer heute genutzt.',
  'overview.unlock': 'Schalte unbegrenzt frei, bis zu 10 h / Datei und 3 GB / Datei.',
  'overview.goUnlimited': 'Unbegrenzte Transkription freischalten',
  'overview.freePlan': 'Free Plan',
  'overview.freeUsage': 'Du hast {{used}} von {{limit}} taeglichen Transkriptionen genutzt',
  'overview.freeLeftFull': 'Du hast heute noch {{count}} kostenlose {{unit}}.',
  'overview.freeLeftRemaining': '{{count}} heute uebrig - weiter gehts.',
  'overview.transcriptionSingular': 'Transkription',
  'overview.transcriptionPlural': 'Transkriptionen',
  'overview.premium': 'Premium',
  'overview.mostPopular': 'Am beliebtesten',
  'overview.pricePeriod': '/ Monat',
  'overview.monthlyPrice': '$9.99/Monat',
  'overview.billedMonthly': 'Jederzeit kuendbar',
  'overview.moneyBack': '30 Tage Geld-zurueck-Garantie \u00b7 abgesichert durch Stripe',
  'overview.shortBenefits': 'Unbegrenzte Transkriptionen \u00b7 Zusammenfassungen \u00b7 genauere Sprecherlabels',
  'overview.upgradeToPremium': 'Auf Premium upgraden',
  'overview.upgradeMonthly': 'Monatlich upgraden',
  'overview.upgradeAnnual': 'Jaehrlich upgraden',
  'overview.premiumPlan': 'Premium Plan',
  'overview.premiumEnjoy': 'Nutze unbegrenzte Transkriptionen mit Premium-Funktionen',
  'overview.freeFeature1': '3 Transkriptionen pro Tag',
  'overview.freeFeature2': 'Max. 35 Minuten pro Datei',
  'overview.freeFeature3': 'Max. 50 MB pro Datei',
  'overview.freeFeature4': 'Erste Transkript-Zusammenfassung inklusive',
  'overview.proFeature1': 'Unbegrenzte Transkriptionen',
  'overview.proFeature2': 'Max. 10 Stunden pro Datei',
  'overview.proFeature3': 'Max. 3 GB pro Datei',
  'overview.proFeature4': 'Transkript-Zusammenfassungen',
  'overview.proFeature5': 'Genauere Sprecherlabels',
  'overview.proFeature6': 'Priority Support',
  'overview.uploadTitle': 'Audio hochladen oder aufnehmen',
  'overview.dragDrop': 'Dateien hier ablegen',
  'overview.selectComputer': '...oder vom Computer auswaehlen',
  'overview.chooseFiles': 'Dateien auswaehlen',
  'overview.sample': 'Beispiel testen',
  'overview.supports': 'Unterstuetzt .mp3, .m4a, .wav, .flac, .ogg, .aiff, .mov, .mp4, YouTube-Untertitel, Google Drive und SoundCloud',
  'overview.or': 'ODER',
  'overview.recordMic': 'Klicken, um Audio direkt aufzunehmen',
  'overview.recordMicInfo': 'Live-Audio mit deinem Mikrofon aufnehmen',
  'overview.recordTabMic': 'Tab + Mikrofon aufnehmen',
  'overview.recordTabMicTitle': 'Anderen Tab und Mikrofon aufnehmen (Chrome/Edge)',
  'overview.recordTabMicInfo': 'Tab-Audio und Mikrofon in eine Audiodatei aufnehmen',
  'overview.recordTab': 'Anderen Tab aufnehmen',
  'overview.recordTabTitle': 'Anderen Tab aufnehmen (Chrome/Edge)',
  'overview.recordTabInfo': 'Audio aus einem Browser-Tab aufnehmen (Chromium)',
  'overview.pasteLink': 'Link einfuegen',
  'overview.urlPlaceholder': 'YouTube, Google Drive, SoundCloud, Podcast oder direkter Medienlink',
  'overview.urlLabel': 'Audio- oder Video-URL',
  'overview.importLink': 'Link importieren',
  'overview.import': 'Importieren',
  'overview.importing': 'Importiert...',
  'overview.publicLinksOnly': 'Nur oeffentliche Links. Unterstuetzt YouTube-Videos mit Untertiteln, direkte Mediendateien, Google Drive, SoundCloud und Podcasts.',
  'overview.freeReassure': '3 kostenlose Transkriptionen/Tag - keine Kreditkarte erforderlich',
  'overview.freeLeftUpgrade': 'Du hast heute noch {{count}} kostenlose {{unit}}. Upgrade jederzeit fuer unbegrenzte Nutzung.',
  'overview.recent': 'Letzte Transkriptionen',
  'overview.noFiles': 'Du hast noch keine Dateien hochgeladen. Lade deine erste Audiodatei hoch, um zu starten.',
  'overview.viewTranscription': 'Transkription ansehen',
  'overview.deleteFile': 'Datei loeschen',
  'overview.dropFiles': 'Audiodateien zum Hochladen ablegen',
  'overview.dropSub': 'Wir starten den Upload sofort.',
  'overview.uploaded': 'Hochgeladen:',
  'overview.duration': 'Dauer:',
  'overview.shortSummary': 'Kurzfassung',
  'overview.eta': 'Restzeit:',
  'overview.etaEstimating': 'Wird geschätzt…',
  'overview.etaAfterStart': 'Restzeit wird nach dem Start geschätzt.',
  'overview.phase.queued': 'Wartet auf den Start der Transkription…',
  'overview.phase.preparing': 'Audio wird vorbereitet…',
  'overview.phase.transcribing': 'Wird transkribiert…',
  'overview.phase.diarizing': 'Sprecher werden erkannt…',
  'overview.phase.saving': 'Transkript wird gespeichert…',
  'overview.phase.completed': 'Transkript fertig',
  'overview.lang': 'Sprache:',
  'overview.statusQueuedWarming': 'wartet / wird vorbereitet',
  'overview.statusStarting': 'Transkription startet...',
  'overview.statusReady': 'bereit zum Starten',
  'overview.statusRequiresUpgrade': 'Upgrade erforderlich',
  'overview.previewReady': "3-Minuten-Vorschau fertig",
  'overview.savedRequiresPremium': "Deine Aufnahme ist gespeichert. Im kostenlosen Tarif sind Dateien bis 35 Minuten möglich. Wähle Premium, um die gesamte Aufnahme zu transkribieren. Sobald Premium aktiv ist, versuchen wir automatisch zu starten; du kannst die Aufnahme auch in deinem Verlauf starten.",
  'billing.signupPremium': "Anmelden & Premium wählen",
  'billing.signupPremiumHint': "Melde dich zuerst an oder erstelle ein Konto. Danach geht es zum Premium-Checkout.",
  'overview.previewNoticePremium': "Dieses Transkript enthält die ersten 3 Minuten. Starte die vollständige Transkription für die gesamte Aufnahme.",
  'overview.previewNotice': "Diese kostenlose Vorschau enthält die ersten 3 Minuten. Mit Premium erhältst du das vollständige Transkript.",
  'overview.previewStarted': "Deine Aufnahme ist gespeichert. Wir transkribieren automatisch die ersten 3 Minuten kostenlos. Das zählt als eine deiner 3 täglichen Transkriptionen. Mit Premium kannst du die vollständige Aufnahme transkribieren.",
  'overview.startFull': "Vollständige Aufnahme transkribieren",

  'overview.upgradeToStart': 'Upgrade zum Starten',
  'overview.startingTranscription': 'Startet...',
  'overview.showSpeakers': 'Sprecher anzeigen',
  'overview.showTimestamps': 'Zeitstempel anzeigen',
  'overview.transcriptViews': 'Transkriptansichten',
  'overview.transcriptTab': 'Transkript',
  'overview.summaryTab': 'Zusammenfassung',
  'overview.summaryPending': 'Die Zusammenfassung wird noch erstellt.',
  'overview.noSummary': 'Keine Zusammenfassung fuer dieses Transkript verfuegbar.',
  'overview.speakerReviewProcessing': 'Sprecherlabels werden verbessert...',
  'overview.speakerReviewProcessingDetail': 'Dein Transkript ist fertig. Wir verbessern noch die Sprecherlabels und aktualisieren diese Ansicht automatisch.',
  'overview.useEnhancedSpeakers': 'Verbesserte Sprecherlabels',
  "overview.speakerNames": "Sprechernamen",
  "overview.renameSpeakers": "Sprecher umbenennen",
  "overview.speakerNamesHint": "Namen werden in allen Sprecherlabels und Exporten übernommen. Ein leeres Feld setzt den Namen zurück.",
  "overview.speakerNamesSaved": "Sprechernamen gespeichert.",
  "overview.speakerNamesUncertain": "Einige Namensschreibweisen sind unsicher. Du kannst sie korrigieren.",
  "overview.speakerNameVariants": "Schreibweisen im Transkript: {{names}}",
  "overview.speakerNameInvalid": "Verwende einen Namen mit 1–60 Zeichen ohne Zeilenumbrüche.",
  "overview.speakerNamesChanged": "Die Sprecherlabels haben sich geändert. Öffne das Transkript erneut und versuche es noch einmal.",
  "overview.speakerNamesSaveFailed": "Sprechernamen konnten nicht gespeichert werden. Deine Eingaben bleiben erhalten; versuche es erneut.",
  "overview.speakerNamesSaving": "Wird gespeichert…",
  "overview.saveSpeakerNames": "Namen speichern",
  'overview.copyText': 'Text kopieren',
  'overview.downloadCompact': 'Download',
  'overview.speakersCompact': 'Sprecher',
  'overview.timestampsCompact': 'Zeitstempel',
  'overview.downloadAs': 'Herunterladen als',
  'overview.txtPlain': 'TXT (einfach)',
  'overview.txtSummary': 'TXT (Zusammenfassung)',
  'overview.summaryTxtOnly': 'Zusammenfassungen koennen nur als TXT heruntergeladen werden.',
  'overview.withSpeakers': '(mit Sprechern)',
  'overview.noSpeakers': '(ohne Sprecher)',
  'overview.withTimecodes': '(mit Zeitcodes)',
  'overview.copied': 'In die Zwischenablage kopiert.',
  'overview.copyErrorTitle': 'Kopierfehler',
  'overview.copyErrorBody': 'Konnte nicht kopiert werden. Bitte Text markieren und manuell kopieren.',
  'overview.exportError': 'Exportfehler',
  'overview.exportFailed': 'Export fehlgeschlagen',
  'overview.downloadingTxt': 'TXT wird heruntergeladen...',
  'overview.downloadingFormat': '{{format}} wird heruntergeladen...',
  'upload.addAudio': 'Audio hinzufuegen',
  'upload.recordTabMicAudio': 'Tab + Mikrofon aufnehmen',
  'upload.recordTabAudio': 'Tab-Audio aufnehmen',
  'upload.recordTranscribe': 'Aufnehmen & transkribieren',
  'upload.selectPodcast': 'Podcast-Folge auswaehlen',
  'upload.close': 'Schliessen',
  'upload.optionsAll': 'Die Optionen unten gelten fuer alle Dateien.',
  'upload.waitingServer': 'Warten auf Server...',
  'upload.uploadingPct': 'Upload... {{percent}}%',
  'upload.filesSelected': '{{count}} Dateien ausgewaehlt',
  'upload.untitled': 'Ohne Titel',
  'upload.file': 'Datei:',
  'upload.size': 'Groesse:',
  'upload.unknown': 'Unbekannt',
  'upload.queued': 'In Warteschlange',
  'upload.uploading': 'Upload',
  'upload.uploaded': 'Hochgeladen',
  'upload.startFailed': 'Start fehlgeschlagen',
  'upload.canceled': 'Abgebrochen',
  'upload.error': 'Fehler',
  'upload.freeSizeExceeded': 'Diese Datei ueberschreitet das Free-Plan-Limit (50 MB). Upgrade fuer bis zu 3 GB und 10 h pro Datei.',
  'upload.premiumSizeExceeded': 'Diese Datei ueberschreitet das Premium-Limit (3 GB). Waehle eine kleinere Datei oder teile sie vor dem Upload.',
  'overview.finalizingUpload': 'Upload abgeschlossen. Die Transkription startet, sobald das Audio bereit ist.',
  'upload.finalizingUpload': 'Audio wird vorbereitet…',
  'upload.transferComplete': 'Upload abgeschlossen',
  'upload.preparation.preparing': 'Audio wird vorbereitet…',
  'upload.preparation.waiting': 'Warten auf die Audio-Vorbereitung…',
  'upload.preparation.checking': 'Audio wird geprüft…',
  'upload.preparation.extracting': 'Audio wird aus dem Video extrahiert…',
  'upload.preparation.converting': 'Audio wird umgewandelt…',
  'upload.preparation.saving': 'Datei wird gespeichert…',
  'upload.preparationElapsed': 'Seit {{seconds}} Sekunden',
  'upload.preparationHint': 'Längere Aufnahmen brauchen etwas mehr Zeit. Du musst die Datei nicht erneut hochladen.',
  'upload.podcastLink': 'Podcast-Link',
  'upload.podcastPlaceholder': 'Apple Podcasts / Show-Seite / RSS-Link einfuegen',
  'upload.resolve': 'Aufloesen',
  'upload.resolving': 'Wird aufgeloest...',
  'upload.feed': 'Feed',
  'upload.directEpisode': 'Direkter Folgen-Link erkannt',
  'upload.change': 'URL aendern',
  'upload.episodes': 'Folgen',
  'upload.filterEpisodes': 'Folgen filtern...',
  'upload.title': 'Titel',
  'upload.date': 'Datum',
  'upload.transcript': 'Transkript',
  'upload.hasTranscript': 'hat Transkript',
  'upload.selected': 'Ausgewaehlt',
  'upload.select': 'Auswaehlen',
  'upload.none': 'keins',
  'upload.pickEpisode': 'Waehle zuerst eine Folge',
  'upload.resolveFailed': 'Link konnte nicht aufgeloest werden',
  'upload.importFailed': 'Import/Transkription fehlgeschlagen',
  'upload.language': 'Sprache:',
  'upload.searchLanguages': 'Sprachen suchen...',
  'upload.advanced': 'Erweiterte Optionen',
  'upload.preferPublisher': 'Publisher-Transkript bevorzugen (falls verfuegbar)',
  'upload.publisherTranscriptInfo': 'Wenn ein Publisher-Transkript verfuegbar ist, wird es sofort importiert.',
  'upload.normalTranscriptionInfo': 'Andernfalls laden wir die Audiodatei und starten eine normale Transkription.',
  'upload.speakerIdentification': 'Sprechererkennung',
  'upload.numberSpeakers': 'Anzahl Sprecher:',
  'upload.autoDetect': 'Automatisch erkennen',
  'upload.oneSpeaker': '1 Sprecher',
  'upload.nSpeakers': '{{count}} Sprecher',
  'upload.importTranscribe': 'Importieren & transkribieren',
  'upload.working': 'Arbeitet...',
  'upload.startRecording': 'Aufnahme starten',
  'upload.startingRecording': 'Aufnahme wird gestartet…',
  'upload.startTabMicCapture': 'Tab + Mikrofon starten',
  'upload.startTabCapture': 'Tab-Aufnahme starten',
  'upload.recording': 'Aufnahme',
  'upload.recordingTabMic': 'Aufnahme (Tab + Mikrofon)',
  'upload.recordingTab': 'Aufnahme (Tab)',
  'upload.recordingComplete': 'Aufnahme abgeschlossen',
  'upload.duration': 'Dauer',
  'upload.resume': 'Fortsetzen',
  'upload.pause': 'Pause',
  'upload.transcribe': 'Transkribieren',
  'upload.captureAnotherTabMic': 'Anderen Tab + Mikrofon aufnehmen',
  'upload.captureAnotherTab': 'Anderen Tab aufnehmen',
  'upload.recordAgain': 'Erneut aufnehmen',
  'upload.uploadingRecording': 'Aufnahme wird hochgeladen... {{percent}}%',
  'upload.generateTimestamps': 'Zeitstempel generieren',
  'upload.premiumPitch': 'Premium: unbegrenzte Transkriptionen, bis zu 10 h/Datei, 3 GB/Datei und Priority Support.',
  'upload.startTranscription': 'Transkription starten',
  'upload.startAll': 'Transkription starten (alle)',
  'upload.startAllCountdown': 'Alle starten ({{count}}s)',
  'upload.startOneCountdown': 'Transkription starten ({{count}}s)',
  'upload.autoStartAllCountdown': 'Transkription fuer alle Dateien startet in {{count}}s',
  'upload.autoStartOneCountdown': 'Transkription startet in {{count}}s',
  'upload.autoStartAll': 'Transkription fuer alle Dateien 10 Sekunden nach Upload/Import automatisch starten',
  'upload.autoStartOne': 'Transkription 10 Sekunden nach Upload/Import automatisch starten',
  'upload.waitingUpload': 'Warten auf Upload...',
  'upload.noTabAudio': 'Es wurde kein Tab-/Systemaudio geteilt. Waehle einen Tab und aktiviere Tab-Audio teilen.',
  'upload.tabMicCaptureFailed': 'Tab- und Mikrofonaufnahme konnte nicht gestartet werden. Bitte Mikrofon erlauben, Tab waehlen und Tab-Audio teilen aktivieren.',
  'upload.tabCaptureFailed': 'Tab-Aufnahme konnte nicht gestartet werden. Bitte Chrome/Edge nutzen und erneut versuchen.',
  'upload.micFailed': 'Mikrofonzugriff fehlgeschlagen. Bitte Browser-Berechtigungen pruefen.',
  'upload.recordingProblemTitle': 'Die Aufnahme konnte nicht starten',
  'upload.recording.micDenied': 'Der Mikrofonzugriff wurde abgebrochen oder blockiert. Erlaube ihn in den Website-Einstellungen deines Browsers und versuche es erneut.',
  'upload.recording.tabDenied': 'Die Tab-Freigabe wurde abgebrochen oder blockiert. Wähle erneut einen Browser-Tab und aktiviere „Tab-Audio teilen“.',
  'upload.recording.noMic': 'Kein Mikrofon gefunden. Verbinde ein Mikrofon und prüfe die Eingabeeinstellungen deines Geräts.',
  'upload.recording.deviceBusy': 'Das Audiogerät konnte nicht geöffnet werden. Prüfe, ob eine andere Anwendung es verwendet, und versuche es erneut.',
  'upload.recording.missingAudio': 'Es wurde kein Tab-Audio geteilt. Wähle einen Browser-Tab und aktiviere „Tab-Audio teilen“.',
  'upload.recording.browser': 'Die Aufnahme ist in diesem Browser nicht verfügbar. Probiere einen anderen Browser oder lade eine Audiodatei hoch.',
  'upload.recording.constraints': 'Dieses Audiogerät unterstützt die gewählten Aufnahmeeinstellungen nicht. Probiere ein anderes Mikrofon oder lade eine Datei hoch.',
  'upload.recording.startFailed': 'Die Aufnahme konnte nicht starten. Versuche es erneut oder lade eine Audiodatei hoch.',
  "profile.backToWorkspace": "Zurück zu Transkripten",
  "profile.accountSecurity": "Konto & Sicherheit",
  "profile.planBilling": "Tarif & Abrechnung",
  "profile.emailVerified": "E-Mail bestätigt",
  "profile.emailPending": "E-Mail noch nicht bestätigt",
  "profile.securityHint": "Vor einer Änderung von E-Mail oder Passwort meldest du dich erneut an.",
  "profile.providerManaged": "Du meldest dich mit {{provider}} an. Verwalte deine Anmeldedaten bei {{provider}}.",
  "profile.active": "Aktiv",
  "profile.premiumWebAccess": "Deine Premium-Funktionen stehen dir in der Web-App zur Verfügung.",
  "profile.upgradeTitle": "Mehr Möglichkeiten mit Premium",
  "profile.openingCheckout": "Checkout wird geöffnet…",
  "profile.openingBilling": "Abrechnung wird geöffnet…",
  "profile.billingHint": "Zahlungsdaten aktualisieren, Tarif ändern oder Abo kündigen.",
  "profile.planLoadFailed": "Deine Tarifdetails konnten nicht geladen werden. Versuche es erneut.",
  "profile.developerTools": "Entwicklertools & API",
  "profile.developerHint": "API-Schlüssel, Nutzung und Integrationen",
  "profile.dataControlsHint": "Transkriptverlauf zurücksetzen oder Konto löschen",
  "profile.apiPlan": "API-Tarif",
  "profile.apiPlanAccess": "Enthält 100 API-Audiostunden pro Abrechnungsmonat. Web-Transkriptionen zählen nicht zur API-Nutzung.",
  "profile.apiPlanHint": "Mehr API-Kapazität benötigt? Entdecke den API-Tarif mit 100 Audiostunden pro Monat.",
  "profile.exploreApiPlan": "API-Tarif ansehen",
  "profile.checkingApiPlan": "API-Tarif wird geprüft…",
  "profile.apiMonthlyQuota": "Monatliche API-Nutzung",
  "profile.plan": "Tarif",
  "profile.included": "Inklusive",
  "profile.used": "Genutzt",
  "profile.remaining": "Verfügbar",
  "profile.resets": "Zurückgesetzt am",
  "profile.refreshing": "Aktualisierung…",
  "profile.refresh": "Aktualisieren",
  "profile.apiQuotaUnavailable": "API-Nutzung nicht verfügbar. Versuche zu aktualisieren.",
  "profile.apiKeyLabel": "Name des API-Schlüssels (optional)",
  "profile.creatingApiKey": "Schlüssel wird erstellt…",
  "profile.loadingApiLogs": "API-Aktivität wird geladen…",
  "profile.viewApiLogs": "API-Aktivität ansehen",
  "profile.recentApiJobs": "Letzte API-Transkriptionen",
  "profile.hide": "Ausblenden",
  "profile.noApiJobs": "Noch keine API-Transkriptionen.",
  "profile.apiKeysLoadFailed": "Deine API-Schlüssel konnten nicht geladen werden. Versuche es erneut.",
  "profile.monthlyCap": "Monatliches Limit für API-Mehrverbrauch (USD)",
  'profile.startPremium': 'Premium starten',
  'profile.title': 'Kontoeinstellungen',
  'profile.subtitle': 'Konto, Einstellungen und Abo verwalten',
  'profile.userInfo': 'Nutzerinformationen',
  'profile.email': 'E-Mail:',
  'profile.premiumSubscription': 'Premium-Abo (unbegrenzte Transkriptionen)',
  'profile.mcpDescription': 'Verbinde einen KI-Assistenten für Audiotranskriptionen und YouTube-Untertitel. MCP nutzt dein API-Kontingent.',
  'profile.mcpConnections': 'Verbundene Assistenten verwalten',
  'profile.mcpSetup': 'Einrichtungsanleitung',
  'profile.premiumAccess': 'Du hast Zugriff auf alle Premium-Funktionen inklusive laengerer Transkriptionen, Priority Support und 15 API-/MCP-Stunden pro Stripe-Abrechnungsmonat.',
  'profile.appleManagedShort': 'Dieses Abo wird von Apple verwaltet. Aendere oder kuendige es in den iPhone/iPad-Einstellungen unter Apple ID -> Abos.',
  'profile.freePlan': 'Free Plan',
  'profile.dailyUsage': 'Taegliche Nutzung: {{used}} von {{limit}} Transkriptionen ({{remaining}} heute uebrig)',
  'profile.securityEmail': 'Sicherheit & E-Mail',
  'profile.emailUnverified': 'Deine E-Mail ist nicht bestaetigt. Bitte bestaetige sie fuer die Kontosicherheit.',
  'profile.resendVerification': 'Bestaetigungs-E-Mail erneut senden',
  'profile.apiKeys': 'API-Schluessel',
  'profile.apiDescription': 'API-Schluessel erlauben direkten Zugriff auf die Transkriptions-API. Halte sie geheim. Du kannst sie jederzeit widerrufen.',
  'profile.apiPlaceholder': 'Optionales Label (z.B. CLI, Zapier)',
  'profile.createApiKey': 'API-Schluessel erstellen',
  'profile.loadingKeys': 'Schluessel werden geladen...',
  'profile.noKeys': 'Noch keine API-Schluessel.',
  'profile.created': 'Erstellt:',
  'profile.lastUsed': 'Zuletzt genutzt:',
  'profile.newApiKey': 'Neuer API-Schluessel (nur einmal sichtbar!)',
  'profile.accountData': 'Kontodaten',
  'profile.accountWarning': 'Die folgenden Aktionen betreffen gespeicherte Daten und koennen nicht rueckgaengig gemacht werden.',
  'profile.noUser': 'Kein Nutzer eingeloggt. Bitte einloggen, um dein Profil zu sehen.',
  'profile.resetAccount': 'Kontodaten zuruecksetzen',
  'profile.deleteAccount': 'Konto loeschen',
  'profile.changePassword': 'Passwort aendern',
  'profile.changeEmail': 'E-Mail aendern',
  'profile.manageSubscription': 'Abo verwalten',
  'profile.viewInvoices': 'Rechnungen anzeigen',
  'profile.hideInvoices': 'Rechnungen ausblenden',
  'profile.invoiceHistory': 'Bisherige Rechnungen',
  'profile.invoice': 'Rechnung',
  'profile.openInvoice': 'Rechnung öffnen',
  'profile.downloadInvoice': 'PDF herunterladen',
  'profile.openInvoiceLabel': 'Rechnung {{number}} öffnen',
  'profile.downloadInvoiceLabel': 'Rechnung {{number}} als PDF herunterladen',
  'profile.loadingInvoices': 'Rechnungen werden geladen…',
  'profile.noInvoices': 'Noch keine Rechnungen vorhanden.',
  'profile.invoicesFailed': 'Rechnungen konnten nicht geladen werden. Bitte versuche es erneut.',
  'profile.retryInvoices': 'Erneut versuchen',
  'profile.olderInvoices': 'Ältere Rechnungen laden',
  'profile.invoiceStatus.paid': 'Bezahlt',
  'profile.invoiceStatus.open': 'Offen',
  'profile.invoiceStatus.draft': 'Entwurf',
  'profile.invoiceStatus.void': 'Storniert',
  'profile.invoiceStatus.uncollectible': 'Uneinbringlich',
  'profile.cancellationScheduled': 'Dein Abo ist zur Kündigung vorgemerkt. Dein Premium-Zugang endet am {{date}}. Unter Abo verwalten kannst du die Kündigung prüfen oder ändern.',
  'profile.billingNeedsAttention': 'Bei deiner Abo-Zahlung besteht Handlungsbedarf. Prüfe deine Zahlungsdaten in der Abo-Verwaltung.',
  'profile.usePremium': 'Premium nutzen: Datei oder Link transkribieren',
  'overview.soundCloudSingleTrack': 'Füge für SoundCloud den öffentlichen Link zu einem einzelnen Track ein (soundcloud.com/artist/track), kein Profil und keine Playlist.',
  "upload.limitTitle": "Bitte prüfe deinen Upload",
  "upload.dailyLimit": "Deine 3 Gast-Transkriptionen für heute sind aufgebraucht. Melde dich an, um mit deinem Konto weiterzumachen, oder komm nach dem täglichen Zurücksetzen wieder.",
  "upload.accountDailyLimit": "Dein kostenloses Tageskontingent ist aufgebraucht. Mit Premium kannst du heute weitermachen oder nach dem täglichen Zurücksetzen wiederkommen.",
  "upload.resetAt": "Kostenlose Transkriptionen sind wieder verfügbar am {{time}}",
  "upload.savedAfterLimit": "Deine Aufnahme ist gespeichert. Starte sie nach dem Zurücksetzen oder einem Upgrade aus deinem Verlauf; du musst sie nicht erneut hochladen.",
  "upload.savedFiles": "Gespeicherte Aufnahme ansehen",
  "upload.continueToday": "Heute mit Premium weitermachen",
  "upload.pendingLimit": "Drei Gast-Uploads warten noch. Starte oder lösche eine Datei in deinem Verlauf, bevor du eine weitere hochlädst.",
  "upload.pendingSessions": "Fünf unvollständige Uploads sind gespeichert. Wähle eine dieser Dateien erneut aus, um sie fortzusetzen, oder brich einen aktiven Upload ab, bevor du einen weiteren hinzufügst.",
  "upload.selectAgain": "Dieser gespeicherte Upload ist abgelaufen oder beendet. Wähle die Datei erneut aus, um einen neuen Upload zu starten.",
  "upload.sessionExpired": "Diese Sitzung ist abgelaufen. Melde dich an, um weiterzumachen, oder erstelle ein kostenloses Konto, falls du noch keines hast.",
  "upload.rateLimited": "Es gab gerade zu viele Anfragen. Warte bitte einen Moment, bevor du es erneut versuchst.",
  "upload.createAccount": "Kostenloses Konto erstellen",
  "upload.signIn": "Anmelden",
  "upload.reviewPending": "Wartende Dateien ansehen",
  "overview.noSpeechTitle": "Keine Sprache erkannt",
  "overview.noSpeechHint": "Die Aufnahme wurde verarbeitet, aber es wurden keine gesprochenen Wörter erkannt. Prüfe, ob die Sprache hörbar ist und das Mikrofon die richtige Person aufgenommen hat. Lade anschließend eine weitere Aufnahme hoch.",
  "overview.silentAudioHint": "Die Aufnahme ist stumm oder extrem leise. Prüfe Mikrofon und Aufnahmelautstärke und lade eine neue Aufnahme hoch.",
  "overview.speechWithoutTextHint": "Es wurde Sprache erkannt, aber keine Wörter transkribiert. Prüfe das Audio und wähle beim nächsten Start die Originalsprache aus.",
  "overview.audioReadHint": "Die Audiodatei konnte für die Diagnose nicht gelesen werden. Prüfe, ob sich die Originaldatei abspielen lässt, und lade sie erneut als MP3, WAV oder M4A hoch.",
  "overview.resultReadHint": "Die Transkriptdateien konnten nicht abgerufen werden. Deine Aufnahme kann Sprache enthalten; dieses Ergebnis bedeutet nicht, dass sie stumm war.",
  "overview.uploadAnotherRecording": "Weitere Aufnahme hochladen",
  "overview.speakerReviewRetrying": "Dein Transkript ist fertig. Die KI-Nachbearbeitung verzögert sich vorübergehend. Wir versuchen es automatisch erneut.",
  "overview.speakerReviewFailedTitle": "KI-Nachbearbeitung nicht verfügbar",
  "overview.speakerReviewFailed": "Dein Transkript ist fertig, aber die zusätzliche KI-Nachbearbeitung konnte nicht abgeschlossen werden. Du kannst den Text lesen und exportieren und die Sprechernamen selbst korrigieren.",
    "import.backgroundWaiting": "Dein Link-Import wartet",
    "import.backgroundDownloading": "Die verlinkte Audiodatei wird geladen",
    "import.backgroundPreparing": "Die importierte Audiodatei wird vorbereitet",
    "import.backgroundReconnecting": "Verbindung zum Import wird wiederhergestellt…",
    "import.backgroundHint": "Der Import läuft im Hintergrund. Du kannst diese Seite verlassen und später zum Arbeitsbereich zurückkehren.",
    "import.backgroundReady": "Dein Link-Import ist bereit",
    "import.alreadyRunning": "Diese Aufnahme wird bereits transkribiert. Du kannst den Fortschritt unten verfolgen.",
    "import.backgroundContinue": "Mit diesem Import fortfahren",
    "import.importedFile": "Importierte Audiodatei",
 "import.captionsMissing": "Für dieses Video sind keine Untertitel verfügbar. Probiere ein anderes Video oder lade die Original-Audiodatei hoch.",
    "import.originalCaptionsMissing": "Untertitel sind nicht in der Originalsprache verfügbar. Lade die Original-Audiodatei zum Transkribieren hoch.",
    "import.videoUnavailable": "Dieses Video ist privat, nicht verfügbar oder eingeschränkt. Probiere ein öffentliches Video oder lade eine Audiodatei hoch.",
    "import.invalidVideo": "Füge einen Link zu einem einzelnen YouTube-Video ein, nicht zu einem Kanal oder einer Playlist.",
    "import.connectionFailed": "Die Quelle ist momentan nicht erreichbar. Versuche es erneut oder lade die Original-Audiodatei hoch.",
    "import.dailyLimit": "Du hast das kostenlose Tageslimit erreicht. Komm morgen wieder oder wähle Premium, um fortzufahren.",
    "import.planLimit": "Dieser Import überschreitet die Grenzen deines Tarifs. Wähle eine kleinere Datei oder wechsle zu Premium.",
    "import.fileTooLarge": "Diese Datei ist zu groß. Lade eine kleinere Datei hoch oder teile die Audiodatei auf.",
    "import.freeFileTooLarge": "Diese Datei überschreitet das Free-Limit von 50 MB. Premium unterstützt bis zu 3 GB pro Datei.",
    "import.premiumFileTooLarge": "Diese Datei überschreitet das Premium-Limit von 3 GB. Lade eine kleinere Datei hoch oder teile die Audiodatei auf.",
    "import.abovePremiumLimit": "Diese Datei überschreitet das Maximum von 3 GB, auch bei Premium. Lade eine kleinere Datei hoch oder teile die Audiodatei auf.",
    "import.accessDenied": "Diese Quelle ist nicht zugänglich. Prüfe, ob der Link öffentlich ist, oder lade die Original-Audiodatei hoch.",
    "import.sourceFailed": "Der Link konnte nicht importiert werden. Prüfe, ob er ohne Anmeldung abspielbar ist, oder lade die Original-Audiodatei hoch.",
    "import.invalidSource": "Füge einen öffentlichen HTTP- oder HTTPS-Link zu einer einzelnen Audio- oder Videodatei ein.",
    "import.sourceNotPublic": "Diese Datei ist nicht öffentlich. Aktiviere die Freigabe für alle mit dem Link oder lade die Originaldatei hoch.",
    "import.sourceAccessDenied": "Die Quelle verweigert unserem Server den Zugriff. Der Link benötigt möglicherweise eine Freigabe, ist abgelaufen oder blockiert Downloads.",
    "import.sourceAccessHelp": "Wenn du die Original-Audio- oder Videodatei hast, lade sie direkt hoch. Prüfe für einen erneuten Link-Import die Freigabe oder verwende einen neuen Download-Link.",
    "import.sourceNotFound": "Diese Datei ist nicht mehr verfügbar. Prüfe den Link oder lade die Originaldatei hoch.",
    "import.unsupportedMedia": "Der Link liefert keine Audio- oder Videodatei. Verwende einen direkten Medienlink oder lade die Originaldatei hoch.",
    "import.webpageMedia": "Dieser Link öffnet eine Webseite. Kopiere den Downloadlink, der die Audio- oder Videodatei direkt öffnet, oder lade die Originaldatei hoch.",
    "import.driveMedia": "Verwende eine freigegebene Audio- oder Videodatei in Google Drive. Google Docs und Ordnerlinks lassen sich nicht transkribieren; lade stattdessen die Originaldatei hoch.",
    "import.driveAccess": "Google Drive erlaubt den Download dieser Datei nicht. Prüfe die Freigabe und kopiere den vollständigen Dateilink erneut. Versuche es dann noch einmal.",
    "import.driveAccessHelp": "Öffne in Drive Freigeben → Allgemeiner Zugriff → Jeder, der über den Link verfügt → Betrachter. Erlaube Betrachtern den Download und kopiere den vollständigen Link über „Link kopieren“. Soll die Datei privat bleiben, lade sie selbst herunter und lade die Originaldatei hier hoch.",
    "import.upgradeContinue": "Premium ansehen und Import fortsetzen",
    "import.upgradeResumeHint": "Dieser Tab behält deinen Link. Nach dem Upgrade wird der Import einmal automatisch fortgesetzt. Dateien über 3 GB müssen vorher verkleinert werden.",
    "import.platformMedia": "Diese Plattformseite liefert keine herunterladbare Mediendatei. Lade deine Audio- oder Videodatei herunter und hier hoch. Bei YouTube verwenden wir verfügbare Untertitel.",
    "import.singleFile": "Füge einen Link zu einer einzelnen Datei oder einem Track ein, nicht zu einem Ordner oder einer Playlist.",
    "import.redirectFailed": "Dieser Link leitet zu oft weiter. Verwende den endgültigen öffentlichen Medienlink oder lade die Originaldatei hoch.",
    "import.sourceTimeout": "Die Quelle hat nicht rechtzeitig geantwortet. Versuche es erneut oder lade die Originaldatei hoch.",
    "import.sourceRateLimited": "Die Quelle begrenzt momentan Downloads. Versuche es später erneut oder lade die Originaldatei hoch.",
    "import.sourceUnavailable": "Der Import aus dieser Quelle ist momentan nicht verfügbar. Lade die Originaldatei hoch.",
    "import.soundCloudNotPublic": "Dieser SoundCloud-Track ist für unseren Server nicht zugänglich. Nutze einen öffentlichen Track, der ohne Anmeldung abspielbar ist, oder lade die Original-Audiodatei hoch.",
    "import.soundCloudNotFound": "Dieser SoundCloud-Track ist nicht mehr verfügbar. Prüfe den Track-Link oder lade die Original-Audiodatei hoch.",
    "import.soundCloudNoAudio": "SoundCloud hat für diesen Track keine abspielbare Audiodatei bereitgestellt. Lade die Original-Audiodatei hoch.",
    "import.soundCloudRegion": "Dieser SoundCloud-Track ist in der Region unseres Servers nicht verfügbar. Lade eine Audiodatei hoch, auf die du zugreifen kannst.",
    "import.linkRestored": "Dein Link wurde behalten. Wähle Importieren, wenn du fortfahren möchtest.",
    "import.captionsImported": "YouTube-Untertitel importiert. Dein Transkript ist fertig.",
    "import.loadingCaptions": "Dein YouTube-Transkript wird geladen…",
    "import.loadingLink": "Dein Link wird importiert…",
    "import.loadingDetail": "Lass dieses Fenster geöffnet. Dein Ergebnis erscheint hier.",
    "import.failedTitle": "Dieser Link konnte nicht importiert werden",
    "workspace.progressUnavailable": "Der Fortschritt konnte nicht aktualisiert werden. Wir versuchen, die Verbindung wiederherzustellen.",
    "workspace.quotaUnavailable": "Dein Transkriptionskontingent konnte nicht geladen werden. Versuche es erneut, um dein aktuelles Kontingent zu sehen.",
    "recovery.title": "Diese Seite konnte nicht angezeigt werden",
    "recovery.body": "Deine gespeicherten Transkripte sind weiterhin verfügbar. Versuche diese Ansicht erneut zu öffnen oder lade die Seite neu, falls das Problem bleibt.",
    "recovery.retry": "Erneut versuchen",
    "recovery.reload": "Seite neu laden",
    'overview.importRecovery': 'Prüfe, ob der Link zu einer einzelnen öffentlichen Audio- oder Videodatei führt, die ohne Anmeldung abspielbar ist. Wenn die Quelle den Import blockiert, lade die Originaldatei hoch, sofern du sie verwenden darfst.',
  'overview.uploadOriginal': 'Stattdessen Originaldatei hochladen',
  'profile.subscribe': 'Premium abonnieren',
  'profile.signOut': 'Ausloggen',
  'profile.verificationSent': 'Bestaetigungs-E-Mail gesendet.',
  'profile.resetConfirmTitle': 'Kontodaten zuruecksetzen',
  'profile.resetConfirmMessage': 'Moechtest du deine Kontodaten wirklich zuruecksetzen? Dein Transkriptionsverlauf wird entfernt. Diese Aktion kann nicht rueckgaengig gemacht werden.',
  'profile.resetConfirm': 'Ja, Daten zuruecksetzen',
  'profile.resetSuccess': 'Deine Kontodaten wurden erfolgreich zurueckgesetzt.',
  'profile.resetFailed': 'Kontodaten konnten nicht zurueckgesetzt werden. Bitte versuche es erneut.',
  'profile.revokeConfirm': 'Diesen API-Schluessel widerrufen? Das kann nicht rueckgaengig gemacht werden.',
  'profile.apiKeyError': 'API-Schluessel-Fehler',
  'profile.apiCreateFailed': 'API-Schluessel konnte nicht erstellt werden.',
  'profile.apiRevokeFailed': 'API-Schluessel konnte nicht widerrufen werden.',
  'profile.subscriptionError': 'Premium-Abo-Fehler',
  'profile.subscriptionFailed': 'Abo fehlgeschlagen: {{message}}',
  'profile.manageTitle': 'Abo verwalten',
  'profile.appleManaged': 'Dein Abo wird von Apple verwaltet. Zum Aendern oder Kuendigen oeffne Einstellungen auf deinem iPhone/iPad -> Apple ID -> Abos.',
  'profile.billingError': 'Abo-Verwaltungsfehler',
  'profile.billingFailed': 'Billing-Portal konnte nicht geoeffnet werden: {{message}}',
  'profile.copiedTitle': 'Kopiert',
  'profile.copiedBody': 'API-Schluessel in die Zwischenablage kopiert.',
  'profile.copyFailedTitle': 'Kopieren fehlgeschlagen',
  'profile.copyFailedBody': 'Konnte nicht kopiert werden.',
};

const fr = {
    'upload.connectionRetrying': 'Connexion interrompue. Nouvelle tentative…',
    'upload.connectionOffline': 'Hors connexion. L’import reprendra au retour de la connexion.',
    'upload.alreadyActive': 'Ce fichier est déjà en cours d’import. Revenez à l’import actif ou sélectionnez-le à nouveau une fois celui-ci terminé.',
    'upload.connectionReconnecting': 'Reconnexion…',
    'upload.connectionResumeHint': 'Votre progression est sauvegardée. L’import reprendra au dernier bloc reçu.',
    'upload.selectSameToResume': 'Import suspendu. Sélectionnez le même fichier pour reprendre à partir de la progression sauvegardée.',
    'upload.authUnavailable': 'Votre connexion au compte n’a pas pu être vérifiée. Réessayez dans un instant.',
    'import.captions_lookup': 'Vérification des sous-titres YouTube disponibles…',
    'import.captions_fetch': 'Téléchargement des sous-titres YouTube…',
    'import.captions_save': 'Sauvegarde de votre transcription YouTube…',
    'import.youtubeWaitingHint': 'Nous vérifions les sous-titres YouTube. Vous pouvez quitter cette page et retrouver le résultat dans votre espace.',
  'workspace.title': 'Votre espace de transcription',
  'workspace.firstSubtitle': 'Commencez avec un fichier, un enregistrement ou un lien public.',
  'workspace.returningSubtitle': 'Commencez quelque chose de nouveau ou retrouvez vos transcriptions.',
  'workspace.premiumActive': 'Premium actif',
  'workspace.newTranscription': 'Nouvelle transcription',
  'workspace.formats': 'MP3, MP4, M4A, WAV et autres fichiers audio ou vidéo',
  'workspace.freeLimits': 'Jusqu’à 35 minutes / 50 Mo par fichier',
  'workspace.premiumLimits': 'Jusqu’à 10 heures / 3 Go par fichier',
  'workspace.recordAudio': 'Microphone',
  'workspace.recordTabMic': 'Onglet + micro',
  'workspace.recordTab': 'Onglet',
  'workspace.urlTitle': 'Lien YouTube ou média',
  'workspace.urlPlaceholder': 'Collez un lien YouTube ou média public',
  'workspace.urlHint': 'Fichiers média publics, Drive, Dropbox ou SoundCloud. YouTube importe les sous-titres disponibles.',
  'workspace.library': 'Vos transcriptions',
  'workspace.exportAll': 'Tout exporter',
  'workspace.emptyTitle': 'Votre première transcription commence ici',
  'workspace.emptyBody': 'Choisissez un fichier ou collez un lien ci-dessus. Vos transcriptions apparaîtront ici, prêtes à lire et à exporter.',
  'workspace.loadingHistory': 'Chargement de vos transcriptions…',
  'workspace.searchingHistory': 'Chargement des transcriptions correspondantes…',
  'workspace.loadMore': 'Charger les anciennes transcriptions',
  'workspace.loadingMore': 'Chargement…',
  'workspace.historyPageHint': 'La recherche couvre toutes vos transcriptions.',
  'upload.preparationBackgroundHint': 'Votre fichier est enregistré sur le serveur. La préparation continue si vous fermez cette page. Revenez dans votre espace pour la démarrer.',
  'import.downloadProgressKnown': '{{received}} sur {{total}} Mo téléchargés',
  'import.downloadProgress': '{{received}} Mo téléchargés',
  'import.longSourceHint': 'Cet enregistrement dépasse la limite gratuite de 35 minutes. Une fois prêt, vous pourrez choisir un aperçu disponible ou passer à Premium.',

  'workspace.historyUnavailable': 'L’historique est temporairement indisponible',
  'workspace.historyError': 'Impossible de charger vos transcriptions. Veuillez réessayer.',
  'workspace.retry': 'Réessayer',
  'workspace.search': 'Rechercher une transcription',
  'workspace.filter': 'Filtrer par statut',
  'workspace.allFiles': 'Toutes',
  'workspace.readyFiles': 'Prêtes à lire',
  'workspace.activeFiles': 'En cours',
  'workspace.noResults': 'Aucune transcription correspondante',
  'workspace.noResultsBody': 'Essayez un autre nom ou modifiez le filtre de statut.',
  'workspace.clearFilters': 'Effacer les filtres',
  'workspace.openTranscript': 'Ouvrir le texte',
  'workspace.openPreview': 'Ouvrir l’aperçu',
  'workspace.openFile': 'Ouvrir la transcription : {{name}}',
  'workspace.deleteFile': 'Supprimer l’enregistrement : {{name}}',
  'workspace.yourPlan': 'Votre offre',
  'workspace.dailyAllowance': 'Quota quotidien',
  'workspace.freeRemaining': '{{remaining}} transcriptions gratuites restantes sur {{limit}} aujourd’hui',
  'workspace.premiumIntro': 'Pour des enregistrements plus longs et des transcriptions plus fréquentes.',
  'workspace.premiumAfterValue': 'Continuez avec des fichiers plus longs et des transcriptions illimitées.',
  'workspace.premiumAtLimit': 'Continuez dès aujourd’hui avec des transcriptions illimitées.',
  'workspace.openingCheckout': 'Ouverture du paiement sécurisé…',
  'workspace.limitReached': 'Votre quota gratuit est épuisé aujourd’hui. Choisissez Premium pour continuer ou revenez après la remise à zéro quotidienne.',
  'workspace.nearLimit': 'Il vous reste une transcription gratuite aujourd’hui. Besoin de plus ?',
  'workspace.seePremium': 'Voir Premium',
  'workspace.accountMenu': 'Menu du compte',
  'common.language': 'Langue',
  'common.back': 'Retour',
  'common.cancel': 'Annuler',
  'common.close': 'Fermer',
  'common.confirm': 'Confirmer',
  'common.copy': 'Copier',
  'common.delete': 'Supprimer',
  'common.download': 'Telecharger',
  'common.email': 'E-mail',
  'common.password': 'Mot de passe',
  'common.loading': 'Chargement...',
  'common.sending': 'Envoi...',
  'common.updating': 'Mise a jour...',
  'common.save': 'Enregistrer',
  'common.upgrade': 'Passer a Premium',
  'common.error': 'Erreur',
  'common.signIn': 'Connexion',
  'common.signOut': 'Deconnexion',
  'common.signUp': 'Inscription',
  'common.profile': 'Profil',
  'common.admin': 'Admin',
  'common.brandTagline': 'Audio & video -> texte',
  'common.done': 'Termine',
  'common.processing': 'Traitement...',
  'common.revoke': 'Revoquer',
  'billing.intervalLabel': 'Frequence de facturation',
  'billing.monthly': 'Mensuel',
  'billing.annual': 'Annuel',
  'billing.savePercent': 'Economisez {{percent}}%',
  'billing.perMonth': '/mois',
  'billing.billedMonthly': 'Facturation mensuelle. Annulation a tout moment.',
  'billing.billedAnnual': 'Facture {{annualTotal}}/an. Annulation a tout moment.',
  'billing.upgradeAnnualDiscount': 'Annuel - economisez {{percent}}%',
  'billing.upgradeMonthly': 'Mensuel',
  'billing.startMonthly': 'Commencer en mensuel pour {{price}}',
  'billing.continueMonthly': 'Continuer la transcription pour {{price}}/mois',
  'billing.saveWithAnnual': 'Economisez {{percent}}% en annuel',
  'billing.annualTeaser': 'A partir de {{price}}/mois avec facturation annuelle.',
  'billing.cancelAnytime': 'Annulation a tout moment.',
  'billing.transcribeFileNow': 'Transcrire ce fichier maintenant',
  'auth.loginTitle': 'Connexion a InstantTranscriber',
  'auth.loginSubtitle': 'Connectez-vous pour continuer vers {{project}}',
  'auth.resetTitle': 'Reinitialiser le mot de passe',
  'auth.resetSubtitle': 'Saisissez votre e-mail et nous vous enverrons un lien.',
  'auth.resetSent': 'Lien envoye. Verifiez votre e-mail.',
  'auth.resetFailed': 'Impossible d envoyer l e-mail. Reessayez.',
  'auth.sendReset': 'Envoyer le lien',
  'auth.signInApple': 'Se connecter avec Apple',
  'auth.signInGoogle': 'Se connecter avec Google',
  'auth.signInEmail': 'Se connecter par e-mail',
  'auth.forgotPassword': 'Mot de passe oublie ?',
  'auth.noAccount': 'Pas encore de compte ?',
  'auth.newToProject': 'Nouveau sur {{project}} ?',
  'auth.createOne': 'Creer un compte',
  'auth.signUpNow': 'S inscrire maintenant',
  'auth.createAccount': 'Creer un compte',
  'auth.signupSubtitle': 'Commencez avec {{project}}',
  'auth.userExists': 'Cet e-mail est deja utilise. Veuillez en choisir un autre.',
  'auth.weakPassword': 'Le mot de passe est trop faible. Utilisez au moins 6 caracteres.',
  'auth.signupSuccess': 'Compte cree. Veuillez confirmer votre e-mail avant de vous connecter.',
  'auth.skip': 'Ignorer pour le moment',
  'auth.signUpGoogle': 'S inscrire avec Google',
  'auth.signUpEmail': 'S inscrire par e-mail',
  'auth.passwordMin': 'Mot de passe (min. 8 caracteres)',
  'auth.alreadyAccount': 'Vous avez deja un compte ?',
  'auth.legal': 'En continuant, vous acceptez les',
  'auth.terms': 'Conditions d utilisation',
  'auth.privacy': 'Politique de confidentialite',
  'auth.or': 'ou',
  'auth.and': 'et',
  'auth.backToSignIn': 'Retour a la connexion',
  'auth.newPassword': 'Nouveau mot de passe',
  'auth.confirmNewPassword': 'Confirmer le nouveau mot de passe',
  'auth.passwordLength': 'Le mot de passe doit contenir au moins 8 caracteres.',
  'auth.passwordMismatch': 'Les mots de passe ne correspondent pas.',
  'auth.passwordUpdated': 'Mot de passe mis a jour.',
  'auth.passwordUpdatedRedirect': 'Mot de passe mis a jour. Redirection vers le tableau de bord...',
  'auth.passwordUpdateFailed': 'Impossible de mettre a jour le mot de passe : {{message}}',
  'auth.chooseNewPassword': 'Choisissez un nouveau mot de passe',
  'auth.checkingResetLink': 'Verification du lien...',
  'auth.invalidResetLink': 'Ce lien est invalide ou expire. Demandez-en un nouveau.',
  'auth.updatePassword': 'Mettre a jour le mot de passe',
  'auth.changePassword': 'Changer le mot de passe',
  'auth.createSecurePassword': 'Creez un nouveau mot de passe securise',
  'auth.backToProfile': 'Retour au profil',
  'auth.changeEmail': 'Changer l e-mail',
  'auth.updateEmailAddress': 'Mettre a jour votre adresse e-mail',
  'auth.emailRequired': 'Veuillez saisir une adresse e-mail.',
  'auth.emailInvalid': 'Veuillez saisir une adresse e-mail valide.',
  'auth.emailSame': 'La nouvelle adresse doit etre differente de l actuelle.',
  'auth.emailChangeSent': 'Un e-mail de verification a ete envoye a votre nouvelle adresse.',
  'auth.emailChangeFailed': 'Impossible de lancer la mise a jour : {{message}}',
  'auth.emailChangeWarning': 'Apres le changement, vous devrez verifier la nouvelle adresse et vous reconnecter.',
  'auth.newEmail': 'Nouvelle adresse e-mail',
  'auth.updateEmail': 'Mettre a jour l e-mail',
  'auth.verifyTitle': 'Verifier votre identite',
  'auth.verifySubtitle': 'Pour des raisons de securite, confirmez votre mot de passe',
  'auth.verifyWarning': 'Cette action necessite une authentification recente.',
  'auth.currentPassword': 'Votre mot de passe actuel',
  'auth.verifyGoogle': 'Verifier avec Google',
  'auth.verifyPassword': 'Verifier le mot de passe',
  'auth.verifying': 'Verification...',
  'auth.authFailed': 'Authentification echouee : {{message}}',
  'auth.googleFailed': 'Connexion Google echouee',
  'auth.deleteTitle': 'Supprimer le compte',
  'auth.deleteSubtitle': 'Cette action est irreversible',
  'auth.deleteWarningTitle': 'Avertissement : suppression definitive',
  'auth.deleteWarningBody': 'La suppression de votre compte supprimera definitivement :',
  'auth.deleteHistory': 'Tout l historique de transcription',
  'auth.deleteSettings': 'Les parametres et preferences du compte',
  'auth.deletePremium': 'Le statut Premium (le cas echeant)',
  'auth.deleting': 'Suppression du compte...',
  'auth.confirmDelete': 'Confirmer la suppression',
  'auth.cancelDelete': 'Annuler et revenir au profil',
  'auth.accountDeleted': 'Votre compte a ete supprime.',
  'auth.deleteFailed': 'La suppression du compte n a pas abouti. Reessayez.',
  'auth.unexpected': 'Une erreur inattendue est survenue. Reessayez.',
  'auth.invalidCredentials': 'E-mail ou mot de passe incorrect.',
  'auth.emailNotConfirmed': 'Veuillez confirmer votre e-mail avant de vous connecter.',
  'overview.loginTitle': 'Connectez-vous pour acceder au tableau de bord',
  'overview.loginBody': 'Vous devez etre connecte pour voir et gerer vos transcriptions.',
  'overview.heroTitle': 'Lancez une transcription en 30 secondes.',
  'overview.heroTitlePremium': 'Lancez une transcription en 30 secondes.',
  'overview.subtitleFree': '3 transcriptions gratuites aujourd hui \u00b7 aucune carte bancaire requise.',
  'overview.subtitlePremium': 'Premium actif \u00b7 transcriptions illimitees \u00b7 jusqu a 10 h / 1 Go par fichier',
  'overview.heroHint': 'Importez un fichier, enregistrez de l audio ou collez un lien public.',
  'overview.usedAllFree': 'Vous avez utilise toutes vos transcriptions gratuites aujourd hui.',
  'overview.almostOutFree': 'Vous avez presque atteint votre limite gratuite du jour.',
  'overview.unlock': 'Debloquez l illimite, jusqu a 10 h / fichier et 1 Go / fichier.',
  'overview.goUnlimited': 'Debloquer la transcription illimitee',
  'overview.freePlan': 'Offre gratuite',
  'overview.freeUsage': 'Vous avez utilise {{used}} sur {{limit}} transcriptions quotidiennes',
  'overview.freeLeftFull': 'Il vous reste {{count}} {{unit}} gratuite(s) aujourd hui.',
  'overview.freeLeftRemaining': '{{count}} restante(s) aujourd hui.',
  'overview.transcriptionSingular': 'transcription',
  'overview.transcriptionPlural': 'transcriptions',
  'overview.premium': 'Premium',
  'overview.mostPopular': 'Le plus populaire',
  'overview.pricePeriod': '/ mois',
  'overview.monthlyPrice': '$9.99/mois',
  'overview.billedMonthly': 'Annulation a tout moment',
  'overview.moneyBack': 'Garantie 30 jours \u00b7 securise par Stripe',
  'overview.shortBenefits': 'Transcriptions illimitees \u00b7 resumes \u00b7 detection des locuteurs plus precise',
  'overview.upgradeToPremium': 'Passer a Premium',
  'overview.upgradeMonthly': 'Passer au mensuel',
  'overview.upgradeAnnual': 'Passer a l annuel',
  'overview.premiumPlan': 'Offre Premium',
  'overview.premiumEnjoy': 'Profitez de transcriptions illimitees avec les fonctions Premium',
  'overview.freeFeature1': '3 transcriptions par jour',
  'overview.freeFeature2': '35 minutes max par fichier',
  'overview.freeFeature3': '50 Mo max par fichier',
  'overview.freeFeature4': 'Premier resume de transcription inclus',
  'overview.proFeature1': 'Transcriptions illimitees',
  'overview.proFeature2': '10 heures max par fichier',
  'overview.proFeature3': '1 Go max par fichier',
  'overview.proFeature4': 'Resumes de transcription',
  'overview.proFeature5': 'Detection des locuteurs plus precise',
  'overview.proFeature6': 'Support prioritaire',
  'overview.uploadTitle': 'Importer ou enregistrer de l audio',
  'overview.dragDrop': 'Glissez-deposez les fichiers ici',
  'overview.selectComputer': '...ou selectionnez depuis votre ordinateur',
  'overview.chooseFiles': 'Choisir des fichiers',
  'overview.sample': 'Essayer un exemple',
  'overview.supports': 'Formats .mp3, .m4a, .wav, .flac, .ogg, .aiff, .mov, .mp4, sous-titres YouTube, Google Drive et SoundCloud',
  'overview.or': 'OU',
  'overview.recordMic': 'Cliquer pour enregistrer directement',
  'overview.recordMicInfo': 'Enregistrer de l audio avec votre microphone',
  'overview.recordTabMic': 'Enregistrer onglet + micro',
  'overview.recordTabMicTitle': 'Enregistrer un autre onglet et le microphone (Chrome/Edge)',
  'overview.recordTabMicInfo': 'Capturer un onglet et votre micro dans un seul fichier audio',
  'overview.recordTab': 'Enregistrer un autre onglet',
  'overview.recordTabTitle': 'Enregistrer un autre onglet (Chrome/Edge)',
  'overview.recordTabInfo': 'Capturer l audio d un onglet du navigateur (Chromium)',
  'overview.pasteLink': 'Coller un lien',
  'overview.urlPlaceholder': 'YouTube, Google Drive, SoundCloud, podcast ou lien media direct',
  'overview.urlLabel': 'URL audio ou video',
  'overview.importLink': 'Importer le lien',
  'overview.import': 'Importer',
  'overview.importing': 'Importation...',
  'overview.publicLinksOnly': 'Liens publics uniquement. Videos YouTube avec sous-titres, fichiers media directs, Google Drive, SoundCloud et podcasts pris en charge.',
  'overview.freeReassure': '3 transcriptions gratuites/jour - aucune carte bancaire requise',
  'overview.freeLeftUpgrade': 'Il vous reste {{count}} {{unit}} gratuite(s) aujourd hui. Passez a Premium a tout moment pour un usage illimite.',
  'overview.recent': 'Transcriptions recentes',
  'overview.noFiles': 'Vous n avez encore importe aucun fichier. Importez votre premier fichier audio pour commencer.',
  'overview.viewTranscription': 'Voir la transcription',
  'overview.deleteFile': 'Supprimer le fichier',
  'overview.dropFiles': 'Deposez vos fichiers audio pour les importer',
  'overview.dropSub': 'Nous lancerons l import immediatement.',
  'overview.uploaded': 'Importe :',
  'overview.duration': 'Duree :',
  'overview.shortSummary': 'Resume court',
  'overview.eta': 'Temps restant :',
  'overview.etaEstimating': 'Estimation en cours…',
  'overview.etaAfterStart': 'Le temps restant sera estimé après le démarrage.',
  'overview.phase.queued': 'En attente du démarrage de la transcription…',
  'overview.phase.preparing': 'Préparation de l’audio…',
  'overview.phase.transcribing': 'Transcription en cours…',
  'overview.phase.diarizing': 'Identification des intervenants…',
  'overview.phase.saving': 'Enregistrement de la transcription…',
  'overview.phase.completed': 'Transcription prête',
  'overview.lang': 'Langue :',
  'overview.statusQueuedWarming': 'en file / preparation',
  'overview.statusStarting': 'demarrage de la transcription...',
  'overview.statusReady': 'pret a demarrer',
  'overview.statusRequiresUpgrade': 'mise a niveau requise',
  'overview.previewReady': "Aperçu de 3 minutes prêt",
  'overview.savedRequiresPremium': "Votre enregistrement est sauvegardé. Le forfait gratuit accepte les fichiers jusqu’à 35 minutes. Choisissez Premium pour transcrire tout l’enregistrement. Nous essaierons de démarrer automatiquement une fois Premium actif ; vous pouvez aussi le lancer depuis votre historique.",
  'billing.signupPremium': "Se connecter et choisir Premium",
  'billing.signupPremiumHint': "Connectez-vous ou créez un compte, puis continuez vers le paiement Premium.",
  'overview.previewNoticePremium': "Cette transcription contient les 3 premières minutes. Lancez la transcription complète pour inclure tout l’enregistrement.",
  'overview.previewNotice': "Cet aperçu gratuit contient les 3 premières minutes. Passez à Premium pour la transcription complète.",
  'overview.previewStarted': "Votre enregistrement est sauvegardé. Nous transcrivons automatiquement les 3 premières minutes gratuitement. Cela compte parmi vos 3 transcriptions quotidiennes. Passez à Premium pour transcrire tout votre enregistrement.",
  'overview.startFull': "Transcrire tout l’enregistrement",

  'overview.upgradeToStart': 'Passer a Premium',
  'overview.startingTranscription': 'Demarrage...',
  'overview.showSpeakers': 'Afficher les locuteurs',
  'overview.showTimestamps': 'Afficher les horodatages',
  'overview.transcriptViews': 'Vues de transcription',
  'overview.transcriptTab': 'Transcription',
  'overview.summaryTab': 'Resume',
  'overview.summaryPending': 'Le resume est encore en preparation.',
  'overview.noSummary': 'Aucun resume disponible pour cette transcription.',
  'overview.speakerReviewProcessing': 'Amelioration des locuteurs...',
  'overview.speakerReviewProcessingDetail': 'Votre transcription est prete. Nous ameliorons encore les libelles de locuteurs et cette vue sera mise a jour automatiquement.',
  'overview.useEnhancedSpeakers': 'Libelles de locuteurs ameliores',
  "overview.speakerNames": "Noms des locuteurs",
  "overview.renameSpeakers": "Renommer les locuteurs",
  "overview.speakerNamesHint": "Les noms sont appliqués à tous les libellés de locuteurs et aux exports. Laissez un champ vide pour réinitialiser le nom.",
  "overview.speakerNamesSaved": "Noms des locuteurs enregistrés.",
  "overview.speakerNamesUncertain": "Certaines orthographes de noms sont incertaines. Vous pouvez les corriger.",
  "overview.speakerNameVariants": "Orthographes dans la transcription : {{names}}",
  "overview.speakerNameInvalid": "Utilisez un nom de 1 à 60 caractères sans saut de ligne.",
  "overview.speakerNamesChanged": "Les libellés ont changé. Rouvrez la transcription et réessayez.",
  "overview.speakerNamesSaveFailed": "Impossible d’enregistrer les noms. Vos modifications sont conservées ; réessayez.",
  "overview.speakerNamesSaving": "Enregistrement…",
  "overview.saveSpeakerNames": "Enregistrer les noms",
  'overview.copyText': 'Copier le texte',
  'overview.downloadCompact': 'Exporter',
  'overview.speakersCompact': 'Locuteurs',
  'overview.timestampsCompact': 'Horodatages',
  'overview.downloadAs': 'Telecharger en',
  'overview.txtPlain': 'TXT (simple)',
  'overview.txtSummary': 'TXT (resume)',
  'overview.summaryTxtOnly': 'Le resume ne peut etre telecharge qu en TXT.',
  'overview.withSpeakers': '(avec locuteurs)',
  'overview.noSpeakers': '(sans locuteurs)',
  'overview.withTimecodes': '(avec timecodes)',
  'overview.copied': 'Copie dans le presse-papiers.',
  'overview.copyErrorTitle': 'Erreur de copie',
  'overview.copyErrorBody': 'Impossible de copier. Selectionnez le texte et copiez-le manuellement.',
  'overview.exportError': 'Erreur d export',
  'overview.exportFailed': 'Export echoue',
  'overview.downloadingTxt': 'Telechargement TXT...',
  'overview.downloadingFormat': 'Telechargement {{format}}...',
  'upload.addAudio': 'Ajouter audio',
  'upload.recordTabMicAudio': 'Enregistrer onglet + micro',
  'upload.recordTabAudio': 'Enregistrer audio d onglet',
  'upload.recordTranscribe': 'Enregistrer & transcrire',
  'upload.selectPodcast': 'Selectionner un episode podcast',
  'upload.close': 'Fermer',
  'upload.optionsAll': 'Les options ci-dessous s appliquent a tous les fichiers.',
  'upload.waitingServer': 'Attente du serveur...',
  'upload.uploadingPct': 'Import... {{percent}}%',
  'upload.filesSelected': '{{count}} fichiers selectionnes',
  'upload.untitled': 'Sans titre',
  'upload.file': 'Fichier :',
  'upload.size': 'Taille :',
  'upload.unknown': 'Inconnue',
  'upload.queued': 'En file',
  'upload.uploading': 'Import',
  'upload.uploaded': 'Importe',
  'upload.startFailed': 'Echec du demarrage',
  'upload.canceled': 'Annule',
  'upload.error': 'Erreur',
  'upload.freeSizeExceeded': 'Ce fichier depasse la limite gratuite (50 Mo). Passez a Premium pour 1 Go et 10 h par fichier.',
  'overview.finalizingUpload': 'Import terminé. La transcription démarrera dès que l’audio sera prêt.',
  'upload.finalizingUpload': 'Préparation de l’audio…',
  'upload.transferComplete': 'Import terminé',
  'upload.preparation.preparing': 'Préparation de l’audio…',
  'upload.preparation.waiting': 'En attente de préparation…',
  'upload.preparation.checking': 'Vérification de l’audio…',
  'upload.preparation.extracting': 'Extraction de l’audio de la vidéo…',
  'upload.preparation.converting': 'Conversion de l’audio…',
  'upload.preparation.saving': 'Enregistrement du fichier…',
  'upload.preparationElapsed': '{{seconds}} secondes écoulées',
  'upload.preparationHint': 'Les longs enregistrements prennent un peu plus de temps. Inutile d’importer à nouveau le fichier.',
  'upload.podcastLink': 'Lien podcast',
  'upload.podcastPlaceholder': 'Coller lien Apple Podcasts / page show / RSS',
  'upload.resolve': 'Resoudre',
  'upload.resolving': 'Resolution...',
  'upload.feed': 'Flux',
  'upload.directEpisode': 'Lien direct d episode detecte',
  'upload.change': 'Changer URL',
  'upload.episodes': 'Episodes',
  'upload.filterEpisodes': 'Filtrer les episodes...',
  'upload.title': 'Titre',
  'upload.date': 'Date',
  'upload.transcript': 'Transcription',
  'upload.hasTranscript': 'a une transcription',
  'upload.selected': 'Selectionne',
  'upload.select': 'Selectionner',
  'upload.none': 'aucun',
  'upload.pickEpisode': 'Selectionnez d abord un episode',
  'upload.resolveFailed': 'Impossible de resoudre le lien',
  'upload.importFailed': 'Import/transcription echoue',
  'upload.language': 'Langue :',
  'upload.searchLanguages': 'Rechercher des langues...',
  'upload.advanced': 'Options avancees',
  'upload.preferPublisher': 'Preferer la transcription editeur (si disponible)',
  'upload.publisherTranscriptInfo': 'Si une transcription editeur est disponible, elle sera importee immediatement.',
  'upload.normalTranscriptionInfo': 'Sinon, nous recuperons l audio et lancons une transcription normale.',
  'upload.speakerIdentification': 'Identification des locuteurs',
  'upload.numberSpeakers': 'Nombre de locuteurs :',
  'upload.autoDetect': 'Detection automatique',
  'upload.oneSpeaker': '1 locuteur',
  'upload.nSpeakers': '{{count}} locuteurs',
  'upload.importTranscribe': 'Importer & transcrire',
  'upload.working': 'Traitement...',
  'upload.startRecording': 'Demarrer l enregistrement',
  'upload.startingRecording': 'Démarrage de l’enregistrement…',
  'upload.startTabMicCapture': 'Demarrer onglet + micro',
  'upload.startTabCapture': 'Demarrer capture d onglet',
  'upload.recording': 'Enregistrement',
  'upload.recordingTabMic': 'Enregistrement (onglet + micro)',
  'upload.recordingTab': 'Enregistrement (onglet)',
  'upload.recordingComplete': 'Enregistrement termine',
  'upload.duration': 'Duree',
  'upload.resume': 'Reprendre',
  'upload.pause': 'Pause',
  'upload.transcribe': 'Transcrire',
  'upload.captureAnotherTabMic': 'Capturer autre onglet + micro',
  'upload.captureAnotherTab': 'Capturer un autre onglet',
  'upload.recordAgain': 'Recommencer',
  'upload.uploadingRecording': 'Import de l enregistrement... {{percent}}%',
  'upload.generateTimestamps': 'Generer les horodatages',
  'upload.premiumPitch': 'Premium : transcriptions illimitees, jusqu a 10 h/fichier, 1 Go/fichier et support prioritaire.',
  'upload.startTranscription': 'Demarrer la transcription',
  'upload.startAll': 'Demarrer la transcription (tous)',
  'upload.startAllCountdown': 'Tout demarrer ({{count}}s)',
  'upload.startOneCountdown': 'Demarrer la transcription ({{count}}s)',
  'upload.autoStartAllCountdown': 'Transcription de tous les fichiers dans {{count}}s',
  'upload.autoStartOneCountdown': 'Transcription dans {{count}}s',
  'upload.autoStartAll': 'Demarrer automatiquement tous les fichiers 10 secondes apres import',
  'upload.autoStartOne': 'Demarrer automatiquement 10 secondes apres import',
  'upload.waitingUpload': 'Attente de l import...',
  'upload.noTabAudio': 'Aucun audio d onglet/systeme partage. Selectionnez un onglet et activez le partage audio.',
  'upload.tabMicCaptureFailed': 'Impossible de demarrer la capture onglet + micro. Autorisez le micro, choisissez un onglet et activez le partage audio.',
  'upload.tabCaptureFailed': 'Impossible de demarrer la capture d onglet. Utilisez Chrome/Edge et reessayez.',
  'upload.micFailed': 'Impossible d acceder au microphone. Verifiez les permissions du navigateur.',
  'upload.recordingProblemTitle': 'Impossible de démarrer l’enregistrement',
  'upload.recording.micDenied': 'L’accès au microphone a été annulé ou bloqué. Autorisez-le dans les paramètres du site de votre navigateur, puis réessayez.',
  'upload.recording.tabDenied': 'Le partage de l’onglet a été annulé ou bloqué. Choisissez un onglet et activez le partage audio.',
  'upload.recording.noMic': 'Aucun microphone trouvé. Connectez-en un et vérifiez les paramètres audio de votre appareil.',
  'upload.recording.deviceBusy': 'Impossible d’ouvrir le périphérique audio. Vérifiez si une autre application l’utilise, puis réessayez.',
  'upload.recording.missingAudio': 'Aucun audio d’onglet partagé. Choisissez un onglet et activez le partage audio.',
  'upload.recording.browser': 'L’enregistrement n’est pas disponible dans ce navigateur. Essayez un autre navigateur ou téléversez un fichier audio.',
  'upload.recording.constraints': 'Ce périphérique audio ne prend pas en charge les paramètres choisis. Essayez un autre microphone ou téléversez un fichier.',
  'upload.recording.startFailed': 'Impossible de démarrer l’enregistrement. Réessayez ou téléversez un fichier audio.',
  "profile.backToWorkspace": "Retour aux transcriptions",
  "profile.accountSecurity": "Compte et sécurité",
  "profile.planBilling": "Offre et facturation",
  "profile.emailVerified": "E-mail vérifié",
  "profile.emailPending": "E-mail non vérifié",
  "profile.securityHint": "Vous devrez vous reconnecter avant de modifier votre e-mail ou votre mot de passe.",
  "profile.providerManaged": "Vous vous connectez avec {{provider}}. Gérez vos identifiants auprès de {{provider}}.",
  "profile.active": "Actif",
  "profile.premiumWebAccess": "Vos fonctionnalités Premium sont disponibles dans l’application web.",
  "profile.upgradeTitle": "Plus de possibilités avec Premium",
  "profile.openingCheckout": "Ouverture du paiement…",
  "profile.openingBilling": "Ouverture de la facturation…",
  "profile.billingHint": "Modifiez votre moyen de paiement, votre offre ou résiliez votre abonnement.",
  "profile.planLoadFailed": "Impossible de charger les détails de votre offre. Réessayez.",
  "profile.developerTools": "Outils de développement et API",
  "profile.developerHint": "Clés API, utilisation et intégrations",
  "profile.dataControlsHint": "Réinitialiser votre historique ou supprimer votre compte",
  "profile.apiPlan": "Offre API",
  "profile.apiPlanAccess": "Inclut 100 heures audio API par mois de facturation. Les transcriptions web ne comptent pas dans l’utilisation de l’API.",
  "profile.apiPlanHint": "Besoin de plus de capacité API ? Découvrez l’offre API avec 100 heures audio incluses par mois.",
  "profile.exploreApiPlan": "Découvrir l’offre API",
  "profile.checkingApiPlan": "Vérification de l’offre API…",
  "profile.apiMonthlyQuota": "Utilisation mensuelle de l’API",
  "profile.plan": "Offre",
  "profile.included": "Inclus",
  "profile.used": "Utilisé",
  "profile.remaining": "Restant",
  "profile.resets": "Réinitialisation",
  "profile.refreshing": "Actualisation…",
  "profile.refresh": "Actualiser",
  "profile.apiQuotaUnavailable": "Utilisation de l’API indisponible. Essayez d’actualiser.",
  "profile.apiKeyLabel": "Nom de la clé API (facultatif)",
  "profile.creatingApiKey": "Création de la clé…",
  "profile.loadingApiLogs": "Chargement de l’activité API…",
  "profile.viewApiLogs": "Voir l’activité API",
  "profile.recentApiJobs": "Transcriptions API récentes",
  "profile.hide": "Masquer",
  "profile.noApiJobs": "Aucune transcription API pour le moment.",
  "profile.apiKeysLoadFailed": "Impossible de charger vos clés API. Réessayez.",
  "profile.monthlyCap": "Plafond mensuel de dépassement API (USD)",
  'profile.startPremium': 'Passer à Premium',
  'profile.title': 'Paramètres du compte',
  'profile.subtitle': 'Gerer votre compte, vos parametres et votre abonnement',
  'profile.userInfo': 'Informations utilisateur',
  'profile.email': 'E-mail :',
  'profile.premiumSubscription': 'Abonnement Premium (transcriptions illimitees)',
  'profile.premiumAccess': 'Vous avez acces a toutes les fonctions Premium, y compris les longues transcriptions, le support prioritaire et 8 heures API par mois de facturation Stripe.',
  'profile.appleManagedShort': 'Cet abonnement est gere par Apple. Utilisez Reglages iPhone/iPad -> Apple ID -> Abonnements pour modifier ou annuler.',
  'profile.freePlan': 'Offre gratuite',
  'profile.dailyUsage': 'Utilisation quotidienne : {{used}} sur {{limit}} transcriptions ({{remaining}} restantes aujourd hui)',
  'profile.securityEmail': 'Securite & e-mail',
  'profile.emailUnverified': 'Votre e-mail n est pas verifie. Veuillez le verifier pour securiser votre compte.',
  'profile.resendVerification': 'Renvoyer l e-mail de verification',
  'profile.apiKeys': 'Cles API',
  'profile.apiDescription': 'Les cles API permettent d appeler directement l API de transcription. Gardez-les secretes. Vous pouvez les revoquer a tout moment.',
  'profile.apiPlaceholder': 'Libelle optionnel (ex. CLI, Zapier)',
  'profile.createApiKey': 'Creer une cle API',
  'profile.loadingKeys': 'Chargement des cles...',
  'profile.noKeys': 'Aucune cle API.',
  'profile.created': 'Cree :',
  'profile.lastUsed': 'Derniere utilisation :',
  'profile.newApiKey': 'Nouvelle cle API (affichee une seule fois !)',
  'profile.accountData': 'Donnees du compte',
  'profile.accountWarning': 'Les actions suivantes affectent vos donnees stockees et sont irreversibles.',
  'profile.noUser': 'Aucun utilisateur connecte. Connectez-vous pour voir votre profil.',
  'profile.resetAccount': 'Reinitialiser les donnees',
  'profile.deleteAccount': 'Supprimer le compte',
  'profile.changePassword': 'Changer le mot de passe',
  'profile.changeEmail': 'Changer l e-mail',
  'profile.manageSubscription': 'Gerer l abonnement',
  'profile.viewInvoices': 'Voir les factures',
  'profile.hideInvoices': 'Masquer les factures',
  'profile.invoiceHistory': 'Historique des factures',
  'profile.invoice': 'Facture',
  'profile.openInvoice': 'Voir la facture',
  'profile.downloadInvoice': 'Télécharger le PDF',
  'profile.openInvoiceLabel': 'Voir la facture {{number}}',
  'profile.downloadInvoiceLabel': 'Télécharger la facture {{number}} au format PDF',
  'profile.loadingInvoices': 'Chargement des factures…',
  'profile.noInvoices': 'Aucune facture pour le moment.',
  'profile.invoicesFailed': 'Impossible de charger les factures. Veuillez réessayer.',
  'profile.retryInvoices': 'Réessayer',
  'profile.olderInvoices': 'Charger les anciennes factures',
  'profile.invoiceStatus.paid': 'Payée',
  'profile.invoiceStatus.open': 'Impayée',
  'profile.invoiceStatus.draft': 'Brouillon',
  'profile.invoiceStatus.void': 'Annulée',
  'profile.invoiceStatus.uncollectible': 'Irrécouvrable',
  'profile.cancellationScheduled': 'La résiliation de votre abonnement est prévue. Votre accès Premium se termine le {{date}}. Gérez votre abonnement pour vérifier ou modifier ce choix.',
  'profile.billingNeedsAttention': 'Le paiement de votre abonnement nécessite votre attention. Ouvrez la gestion de facturation pour vérifier vos informations.',
  'profile.usePremium': 'Utiliser Premium : transcrire un fichier ou un lien',
  'overview.soundCloudSingleTrack': 'Pour SoundCloud, collez le lien public d’un seul titre (soundcloud.com/artiste/titre), pas un profil ni une playlist.',
  "upload.limitTitle": "Vérifiez votre importation",
  "upload.dailyLimit": "Vos 3 transcriptions invitées du jour sont épuisées. Connectez-vous pour continuer avec votre compte, ou revenez après la remise à zéro quotidienne.",
  "upload.accountDailyLimit": "Votre quota quotidien gratuit est épuisé. Choisissez Premium pour continuer aujourd’hui, ou revenez après la remise à zéro quotidienne.",
  "upload.resetAt": "Les transcriptions gratuites seront à nouveau disponibles le {{time}}",
  "upload.savedAfterLimit": "Votre enregistrement est conservé. Lancez-le depuis votre historique après la remise à zéro ou le passage à Premium, sans le téléverser à nouveau.",
  "upload.savedFiles": "Voir l’enregistrement conservé",
  "upload.continueToday": "Continuer aujourd’hui avec Premium",
  "upload.pendingLimit": "Trois imports invités sont en attente. Lancez ou supprimez un fichier dans votre historique avant d’en ajouter un autre.",
  "upload.pendingSessions": "Cinq imports inachevés sont enregistrés. Sélectionnez à nouveau l’un de ces fichiers pour le reprendre, ou annulez un import actif avant d’en ajouter un autre.",
  "upload.selectAgain": "Cet import enregistré a expiré ou est terminé. Sélectionnez à nouveau le fichier pour lancer un nouvel import.",
  "upload.sessionExpired": "Cette session a expiré. Connectez-vous pour continuer, ou créez un compte gratuit si vous n’en avez pas encore.",
  "upload.rateLimited": "Trop de requêtes pour le moment. Patientez avant de réessayer.",
  "upload.createAccount": "Créer un compte gratuit",
  "upload.signIn": "Se connecter",
  "upload.reviewPending": "Voir les fichiers en attente",
  "overview.noSpeechTitle": "Aucune parole reconnue",
  "overview.noSpeechHint": "L’enregistrement a été traité, mais aucun mot parlé n’a été reconnu. Vérifiez que la voix est audible et que le microphone a enregistré la bonne personne, puis importez un autre enregistrement.",
  "overview.silentAudioHint": "L’audio est silencieux ou extrêmement faible. Vérifiez le microphone et le volume, puis importez un nouvel enregistrement.",
  "overview.speechWithoutTextHint": "Des paroles ont été détectées, mais aucun mot n’a été reconnu. Vérifiez l’audio et sélectionnez sa langue originale au prochain lancement.",
  "overview.audioReadHint": "L’audio n’a pas pu être décodé pour le diagnostic. Vérifiez que le fichier original se lit, puis importez-le en MP3, WAV ou M4A.",
  "overview.resultReadHint": "Les fichiers de transcription n’ont pas pu être récupérés. Votre enregistrement peut contenir de la parole ; ce résultat ne prouve pas qu’il était silencieux.",
  "overview.uploadAnotherRecording": "Importer un autre enregistrement",
  "overview.speakerReviewRetrying": "Votre transcription est prête. La révision par IA est temporairement retardée ; nous réessayons automatiquement.",
  "overview.speakerReviewFailedTitle": "Révision par IA indisponible",
  "overview.speakerReviewFailed": "Votre transcription est prête, mais la révision supplémentaire par IA n’a pas pu aboutir. Vous pouvez lire et exporter le texte, et corriger les noms des locuteurs.",
    "import.backgroundWaiting": "Votre import est en attente",
    "import.backgroundDownloading": "Téléchargement du fichier audio",
    "import.backgroundPreparing": "Préparation du fichier audio importé",
    "import.backgroundReconnecting": "Reconnexion à votre import…",
    "import.backgroundHint": "L’import continue en arrière-plan. Vous pouvez quitter cette page et revenir à votre espace.",
    "import.backgroundReady": "Votre import est prêt",
    "import.alreadyRunning": "Cet enregistrement est déjà en cours de transcription. Suivez sa progression ci-dessous.",
    "import.backgroundContinue": "Continuer avec cet import",
    "import.importedFile": "Audio importé",
 "import.captionsMissing": "Cette vidéo ne dispose pas de sous-titres. Essayez une autre vidéo ou téléversez le fichier audio original.",
    "import.originalCaptionsMissing": "Les sous-titres ne sont pas disponibles dans la langue originale. Téléversez l’audio original pour le transcrire.",
    "import.videoUnavailable": "Cette vidéo est privée, indisponible ou restreinte. Essayez une vidéo publique ou téléversez un fichier audio.",
    "import.invalidVideo": "Collez le lien d’une seule vidéo YouTube, plutôt que celui d’une chaîne ou d’une playlist.",
    "import.connectionFailed": "La source est actuellement inaccessible. Réessayez ou téléversez le fichier audio original.",
    "import.dailyLimit": "Vous avez atteint la limite quotidienne gratuite. Revenez demain ou choisissez Premium pour continuer.",
    "import.planLimit": "Cette importation dépasse les limites de votre forfait. Choisissez un fichier plus petit ou passez à Premium.",
    "import.fileTooLarge": "Ce fichier est trop volumineux. Téléversez un fichier plus petit ou divisez l’audio.",
    "import.freeFileTooLarge": "Ce fichier dépasse la limite de 50 Mo du forfait gratuit. Premium accepte jusqu’à 3 Go par fichier.",
    "import.premiumFileTooLarge": "Ce fichier dépasse la limite Premium de 3 Go. Téléversez un fichier plus petit ou divisez l’audio.",
    "import.abovePremiumLimit": "Ce fichier dépasse le maximum de 3 Go, même avec Premium. Téléversez un fichier plus petit ou divisez l’audio.",
    "import.accessDenied": "Cette source est inaccessible. Vérifiez que le lien est public ou téléversez le fichier audio original.",
    "import.sourceFailed": "Impossible d’importer ce lien. Vérifiez qu’il est lisible sans connexion ou téléversez le fichier audio original.",
    "import.invalidSource": "Collez un lien HTTP ou HTTPS public vers un seul fichier audio ou vidéo.",
    "import.sourceNotPublic": "Ce fichier n’est pas public. Activez le partage avec toute personne disposant du lien ou téléversez le fichier original.",
    "import.sourceAccessDenied": "La source refuse l’accès à notre serveur. Le lien peut nécessiter une autorisation, avoir expiré ou bloquer les téléchargements.",
    "import.sourceAccessHelp": "Si vous avez le fichier audio ou vidéo original, importez-le directement. Pour réessayer avec un lien, vérifiez les autorisations de partage ou utilisez un nouveau lien de téléchargement.",
    "import.sourceNotFound": "Ce fichier n’est plus disponible. Vérifiez le lien ou téléversez le fichier original.",
    "import.unsupportedMedia": "Ce lien ne renvoie pas de fichier audio ou vidéo. Utilisez un lien direct ou téléversez le fichier original.",
    "import.webpageMedia": "Ce lien ouvre une page web. Copiez le lien de téléchargement qui ouvre directement le fichier audio ou vidéo, ou téléversez le fichier original.",
    "import.driveMedia": "Utilisez un fichier audio ou vidéo partagé dans Google Drive. Les documents Google et les dossiers ne peuvent pas être transcrits ; téléversez le média original.",
    "import.driveAccess": "Google Drive n’a pas autorisé le téléchargement de ce fichier. Vérifiez le partage, recopiez le lien complet du fichier, puis réessayez.",
    "import.driveAccessHelp": "Dans Drive, ouvrez Partager → Accès général → Tous les utilisateurs disposant du lien → Lecteur. Autorisez le téléchargement et recopiez le lien complet avec « Copier le lien ». Pour garder le fichier privé, téléchargez-le vous-même puis importez l’original ici.",
    "import.upgradeContinue": "Voir Premium et reprendre l’import",
    "import.upgradeResumeHint": "Cet onglet conserve votre lien. Après la mise à niveau, l’import reprend automatiquement une seule fois. Les fichiers de plus de 3 Go doivent d’abord être réduits.",
    "import.platformMedia": "Cette page de plateforme ne fournit pas de média téléchargeable. Téléchargez votre audio ou vidéo, puis importez le fichier. Les liens YouTube utilisent les sous-titres disponibles.",
    "import.singleFile": "Collez le lien d’un seul fichier ou morceau, plutôt que celui d’un dossier ou d’une playlist.",
    "import.redirectFailed": "Ce lien redirige trop souvent. Utilisez le lien public final ou téléversez le fichier original.",
    "import.sourceTimeout": "La source n’a pas répondu à temps. Réessayez ou téléversez le fichier original.",
    "import.sourceRateLimited": "La source limite temporairement les téléchargements. Réessayez plus tard ou téléversez le fichier original.",
    "import.sourceUnavailable": "L’importation depuis cette source est temporairement indisponible. Téléversez le fichier original.",
    "import.soundCloudNotPublic": "Ce morceau SoundCloud n’est pas accessible à notre serveur. Utilisez un morceau public lisible sans connexion ou téléversez l’audio original.",
    "import.soundCloudNotFound": "Ce morceau SoundCloud n’est plus disponible. Vérifiez le lien ou téléversez l’audio original.",
    "import.soundCloudNoAudio": "SoundCloud n’a pas fourni d’audio lisible pour ce morceau. Téléversez l’audio original.",
    "import.soundCloudRegion": "Ce morceau SoundCloud n’est pas disponible dans la région de notre serveur. Téléversez un fichier audio auquel vous avez accès.",
    "import.linkRestored": "Votre lien a été conservé. Sélectionnez Importer pour continuer.",
    "import.captionsImported": "Sous-titres YouTube importés. Votre transcription est prête.",
    "import.loadingCaptions": "Chargement de votre transcription YouTube…",
    "import.loadingLink": "Importation de votre lien…",
    "import.loadingDetail": "Gardez cette fenêtre ouverte. Votre résultat apparaîtra ici.",
    "import.failedTitle": "Impossible d’importer ce lien",
    "workspace.progressUnavailable": "Impossible d’actualiser la progression. Nous essayons de rétablir la connexion.",
    "workspace.quotaUnavailable": "Impossible de charger votre quota. Réessayez pour voir votre quota de transcription actuel.",
    "recovery.title": "Impossible d’afficher cette page",
    "recovery.body": "Vos transcriptions enregistrées restent disponibles. Réessayez d’ouvrir cette vue ou rechargez la page si le problème persiste.",
    "recovery.retry": "Réessayer",
    "recovery.reload": "Recharger la page",
    'overview.importRecovery': 'Vérifiez que le lien correspond à un seul contenu audio ou vidéo public, lisible sans connexion. Si la source bloque l’importation, téléversez le fichier original que vous avez le droit d’utiliser.',
  'overview.uploadOriginal': 'Téléverser le fichier original à la place',
  'profile.subscribe': 'S abonner a Premium',
  'profile.signOut': 'Deconnexion',
  'profile.verificationSent': 'E-mail de verification envoye.',
  'profile.resetConfirmTitle': 'Reinitialiser les donnees',
  'profile.resetConfirmMessage': 'Voulez-vous vraiment reinitialiser les donnees du compte ? Tout l historique sera supprime. Cette action est irreversible.',
  'profile.resetConfirm': 'Oui, reinitialiser',
  'profile.resetSuccess': 'Les donnees du compte ont ete reinitialisees.',
  'profile.resetFailed': 'Impossible de reinitialiser les donnees. Reessayez.',
  'profile.revokeConfirm': 'Revoquer cette cle API ? Cette action est irreversible.',
  'profile.apiKeyError': 'Erreur de cle API',
  'profile.apiCreateFailed': 'Impossible de creer la cle API.',
  'profile.apiRevokeFailed': 'Impossible de revoquer la cle API.',
  'profile.subscriptionError': 'Erreur abonnement Premium',
  'profile.subscriptionFailed': 'Abonnement echoue : {{message}}',
  'profile.manageTitle': 'Gerer l abonnement',
  'profile.appleManaged': 'Votre abonnement est gere par Apple. Pour modifier ou annuler, ouvrez Reglages sur iPhone/iPad -> Apple ID -> Abonnements.',
  'profile.billingError': 'Erreur de gestion abonnement',
  'profile.billingFailed': 'Impossible d acceder au portail de facturation : {{message}}',
  'profile.copiedTitle': 'Copie',
  'profile.copiedBody': 'Cle API copiee dans le presse-papiers.',
  'profile.copyFailedTitle': 'Copie echouee',
  'profile.copyFailedBody': 'Impossible de copier.',
};

function mergeLanguage(base, overrides) {
  return { ...base, ...overrides };
}

Object.assign(translations.en, {
  "overview.bulkAllFailed": "None of the selected transcripts could be exported. Please try again.",
  "overview.export_auth_required": "Your session expired. Please sign in again and restart the export.",
  "overview.export_account_changed": "The account changed. Restart the export in the original account.",
  "overview.export_cancelled": "The export was cancelled. You can start it again."
});
Object.assign(de, {
  "overview.bulkAllFailed": "Keines der ausgewählten Transkripte konnte exportiert werden. Bitte versuche es erneut.",
  "overview.export_auth_required": "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an und starte den Export noch einmal.",
  "overview.export_account_changed": "Das Konto wurde gewechselt. Starte den Export im ursprünglichen Konto erneut.",
  "overview.export_cancelled": "Der Export wurde abgebrochen. Du kannst ihn erneut starten."
});
Object.assign(fr, {
  "overview.bulkAllFailed": "Aucune des transcriptions sélectionnées n’a pu être exportée. Veuillez réessayer.",
  "overview.export_auth_required": "Votre session a expiré. Reconnectez-vous, puis relancez l’export.",
  "overview.export_account_changed": "Le compte a changé. Relancez l’export depuis le compte d’origine.",
  "overview.export_cancelled": "L’export a été annulé. Vous pouvez le relancer."
});
translations.de = mergeLanguage(translations.en, de);
translations.fr = mergeLanguage(translations.en, fr);

// Recording controls share the same locale and interpolation as the workspace.
for (const [locale, copy] of Object.entries(recordingAudioCopy)) {
  for (const [key, value] of Object.entries(copy)) translations[locale][`upload.audioSources.${key}`] = value;
}

export function normalizeLanguage(language) {
  const value = String(language || '').toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.includes(value) ? value : DEFAULT_LANGUAGE;
}

function detectLanguage() {
  try {
    const requested = new URLSearchParams(window.location.search).get('lang');
    if (SUPPORTED_LANGUAGES.includes(requested)) return requested;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return normalizeLanguage(stored);
  } catch (_) {}
  return normalizeLanguage(navigator.languages?.[0] || navigator.language);
}

function interpolate(value, params = {}) {
  return String(value).replace(/\{\{(\w+)\}\}/g, (_, key) => (
    params[key] == null ? '' : String(params[key])
  ));
}

const I18nContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key, params) => interpolate(translations.en[key] || key, params),
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(DEFAULT_LANGUAGE);

  useEffect(() => {
    setLanguageState(detectLanguage());
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguageState(normalized);
    try {
      window.localStorage.setItem(STORAGE_KEY, normalized);
    } catch (_) {}
    document.cookie = `${STORAGE_KEY}=${normalized}; path=/; max-age=31536000; SameSite=Lax`;
  };

  const value = useMemo(() => ({
    language,
    setLanguage,
    t: (key, params) => interpolate(translations[language]?.[key] || translations.en[key] || key, params),
  }), [language]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
